import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
// Removed debounce import as it's no longer used
import { supplements } from '../constants/supplements';
import { catalogApi } from '../services/catalogApi';
import { clientApi } from '../services/clientApi';
import { bookingApi } from '../services/bookingApi';
import { onlineBookingApi } from '../services/onlineBookingApi';
import { PlanningApi } from '../services/planningApi';
import {
  cleanDateForApi,
  formatPhoneAsYouType,
  htmlToText,
  parseInternationalPhone,
  PIERCING_DEPOSIT_AMOUNT_EUR,
  sanitizeRichTextHtml,
} from '@digitaltattoo/shared-utils';
import {
  BodyZone,
  CatalogShop,
  ExistingClient,
  PiercingItem,
  PiercingTypeEntity,
  ValidationErrors,
} from '@digitaltattoo/types';
import { CustomerDetailsStep, ClientData } from '../components/CustomerDetailsStep';
import { GuestBookingForm } from '../components/GuestBookingForm';
import { RichTextEditor } from '../components/RichTextEditor';
import { PiercingZoneGuidePanel } from '../components/PiercingZoneGuidePanel';
import { ThemeSelect } from '../components/ThemeSelect';
import { BookingStepper } from '../components/BookingStepper';
import { LandscapeHint } from '../components/LandscapeHint';
import { useSystemStatus } from '../hooks/useTestMode';
import { useScrollOnStepChange } from '../hooks/useScrollOnStepChange';
import { scrollBookingIntoView } from '../utils/scrollBookingIntoView';
import { filterPastSlotsForToday } from '../utils/slotFilters';
import { hasVisibleHtmlContent } from '../utils/htmlContent';
import { getPiercingZoneGuide } from '../utils/piercingZoneGuides';
import {
  getOrCreatePiercingJourneyId,
  mapPiercingStepToJourneyStage,
} from '../utils/piercingBookingJourney';
import { bookingJourneyApi } from '../services/bookingJourneyApi';
import {
  ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS,
  type PiercingPaymentMethod,
} from '../constants/paymentMethods';
import { ModalHeader } from '../components/ModalHeader';
import { LegalPageContent } from './LegalInfoPage';
import { RefundPolicyContent } from './RefundPolicyPage';
import { DateInput } from '../components/DateInput';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { 
  ArrowLeft, 
  ArrowRight, 
  MapPin, 
  User, 
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Trash2,
  Phone,
  Mail,
  Loader2,
  Shield,
  Copy
} from 'lucide-react';

// Keep combination rules in code, but disable enforcement for now.
const PIERCING_COMBINATION_RULES_ENABLED = false;

interface BookingFormData {
  // Multi-piercing cart
  piercings: BookingPiercingItem[];
  
  // UI state for current piercing selection (before adding to cart)
  bodyZoneId: string;
  piercingTypeId: string;
  selectedSupplements: string[];
  noteToPractitioner: string;
  
  // Step 3: Shop & Time Selection
  shopId: string;
  availableDate: string;
  selectedTimeSlot: string;
  practitionerId?: string;
  
  // Step 4: Contact Details (now handled by CustomerDetailsStep)
  clientId?: string;
  preferredLanguage?: 'fr' | 'en';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor: boolean | null;
  isAnonymous?: boolean;
  anonymousCode?: string;
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  
  // Step 5: Payment
  paymentMethod: PiercingPaymentMethod;
  totalPrice: number;
  depositAmount: number;
  
  // Additional
  notes: string;
}

// Helper function to safely format date for date input
const formatDateForInput = (dateValue: any): string => {
  if (!dateValue) return '';
  
  try {
    let date: Date;
    
    if (typeof dateValue === 'string') {
      if (!dateValue.trim()) return '';
      date = new Date(dateValue);
    } else if (typeof dateValue === 'object' && dateValue !== null) {
      return '';
    } else {
      date = new Date(dateValue);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateValue);
      return '';
    }
    
    // Check for default/placeholder dates (before year 1950)
    if (date.getFullYear() < 1950) {
      return '';
    }
    
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Error formatting date:', dateValue, error);
    return '';
  }
};

/** Slot API returns shop-local times as yyyy-MM-ddTHH:mm:ss (no timezone suffix). */
const extractSlotLocalTime = (slotStart: string): string => {
  const timePart = slotStart.includes('T') ? slotStart.split('T')[1] : slotStart;
  return timePart.slice(0, 5);
};

const RESUME_STORAGE_KEY = 'piercingBookingResume';
const CADDY_STORAGE_KEY = 'piercingBookingCaddy';

interface BookingPiercingItem extends PiercingItem {
  /** Snapshot used by payment/confirmation summaries while catalog data reloads. */
  piercingTypeName?: string;
  bodyZoneName?: string;
  basePrice?: number;
}

interface PersistedBookingDraft {
  currentStep: number;
  formData: Partial<BookingFormData>;
  contactInfo: { email: string; phone: string };
  piercingCart: BookingPiercingItem[];
}

function getDefaultBookingFormData(): BookingFormData {
  return {
    piercings: [],
    bodyZoneId: '',
    piercingTypeId: '',
    selectedSupplements: [],
    noteToPractitioner: '',
    availableDate: '',
    shopId: '',
    selectedTimeSlot: '',
    practitionerId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    paymentMethod: 'card',
    totalPrice: 0,
    depositAmount: 0,
    notes: ''
  };
}

function readPersistedBookingDraft(): PersistedBookingDraft | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(RESUME_STORAGE_KEY);
    const fallback = localStorage.getItem(CADDY_STORAGE_KEY);
    const raw = stored || fallback;
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (typeof parsed?.currentStep === 'number' && parsed.currentStep >= 9) {
      sessionStorage.removeItem(RESUME_STORAGE_KEY);
      localStorage.removeItem(CADDY_STORAGE_KEY);
      return null;
    }

    return {
      currentStep: typeof parsed?.currentStep === 'number' ? parsed.currentStep : 3,
      formData: parsed?.formData && typeof parsed.formData === 'object' ? parsed.formData : {},
      contactInfo: parsed?.contactInfo && typeof parsed.contactInfo === 'object'
        ? {
            email: parsed.contactInfo.email || '',
            phone: parsed.contactInfo.phone || '',
          }
        : { email: '', phone: '' },
      piercingCart: Array.isArray(parsed?.piercingCart) ? parsed.piercingCart : [],
    };
  } catch (error) {
    console.warn('Failed to read piercing booking session:', error);
    return null;
  }
}

function isFreshBookingStart(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('fresh') === '1';
}

const formatPersonName = (firstName?: string | null, lastName?: string | null): string =>
  [firstName, lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

export function PiercingBookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const {
    isTestMode,
    piercingBookingZoneGuideEnabled,
    piercingBookingAnonymousEnabled,
    loading: systemStatusLoading,
  } = useSystemStatus();
  const shouldStartFresh = searchParams.get('fresh') === '1';
  const journeyIdRef = useRef(getOrCreatePiercingJourneyId(shouldStartFresh));

  const [currentStep, setCurrentStep] = useState(() => {
    if (isFreshBookingStart()) return 3;
    return readPersistedBookingDraft()?.currentStep ?? 3;
  });
  const [bodyZones, setBodyZones] = useState<BodyZone[]>([]);
  const [piercingTypes, setPiercingTypes] = useState<PiercingTypeEntity[]>([]);
  const [minorOnlyZoneIds, setMinorOnlyZoneIds] = useState<ReadonlySet<string>>(new Set());
  const [shops, setShops] = useState<CatalogShop[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
const [contactPhoneCountry, setContactPhoneCountry] =
  useState<SupportedPhoneCountry>('FR');

const [contactPhoneDisplay, setContactPhoneDisplay] =
  useState('');

const [contactPhoneE164, setContactPhoneE164] =
  useState<string | null>(null);

const [contactPhoneIsValid, setContactPhoneIsValid] = useState(false);
  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openLegalModal, setOpenLegalModal] = useState<'refundPolicy' | 'terms' | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);
  const [existingClient, setExistingClient] = useState<ExistingClient | null>(null);
  const [showExistingClientModal, setShowExistingClientModal] = useState(false);
  const [clientCheckStep, setClientCheckStep] = useState<'input' | 'existing' | 'otp' | 'form'>('input');
  const [contactInfo, setContactInfo] = useState(() => {
    if (isFreshBookingStart()) return { email: '', phone: '' };
    return readPersistedBookingDraft()?.contactInfo ?? { email: '', phone: '' };
  });

  useEffect(() => {
    if (!openLegalModal) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenLegalModal(null);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openLegalModal]);

  // Keep the phone widget's parsing state aligned with values restored from a
  // draft or supplied by an authenticated account. Without this, a restored
  // phone number looks filled but is still considered invalid internally.
  useEffect(() => {
    if (contactInfo.phone === contactPhoneDisplay) return;

    if (!contactInfo.phone.trim()) {
      setContactPhoneDisplay('');
      setContactPhoneE164(null);
      setContactPhoneIsValid(false);
      return;
    }

    const parsedPhone = parseInternationalPhone(
      contactInfo.phone,
      contactPhoneCountry,
    );
    const country = parsedPhone.country ?? contactPhoneCountry;

    setContactPhoneCountry(country);
    setContactPhoneDisplay(formatPhoneAsYouType(contactInfo.phone, country));
    setContactPhoneE164(parsedPhone.isValid ? parsedPhone.e164 : null);
    setContactPhoneIsValid(parsedPhone.isValid);
  }, [contactInfo.phone, contactPhoneCountry, contactPhoneDisplay]);
  // Removed checkingClient state as we no longer do real-time client checking
  const [existingClientFound, setExistingClientFound] = useState<any>(null);
  const [showClientConfirmation, setShowClientConfirmation] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState<number | null>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendLimitReached, setResendLimitReached] = useState(false);
  const [otpChannel, setOtpChannel] = useState<'email' | 'sms'>('email');
  const [otpDestinationHint, setOtpDestinationHint] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [depositPending, setDepositPending] = useState(false);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [contactLookupStatus, setContactLookupStatus] = useState<'unknown' | 'not_found' | 'found' | 'create_new'>('unknown');
  const [clientCreatedNotice, setClientCreatedNotice] = useState<'account' | 'guest' | 'anonymous' | null>(null);
  const [anonymousCodeCopied, setAnonymousCodeCopied] = useState(false);
  const [anonymousFormBusy, setAnonymousFormBusy] = useState(false);
  const [showClientCreatedToast, setShowClientCreatedToast] = useState(false);
  const [bookingConflictMessage, setBookingConflictMessage] = useState<string | null>(null);
  const [bookingConflictSelection, setBookingConflictSelection] = useState<{
    date: string;
    time: string;
  } | null>(null);
  const [piercingConflict, setPiercingConflict] = useState<{
    minGapHours: number;
    conflictScope: 'date' | 'time';
    conflictReason?: 'shop_unavailable' | 'client_gap';
    conflictAppointment?: { scheduledAt: string; piercings: string[]; notes?: string };
  } | null>(null);
  const [checkingPiercingConflict, setCheckingPiercingConflict] = useState(false);
  const lastPiercingConflictKey = useRef<string | null>(null);
  const bookingTopRef = useRef<HTMLDivElement>(null);
  const resumeStateRef = useRef<{
    currentStep: number;
    formData: BookingFormData;
    contactInfo: { email: string; phone: string };
    piercingCart: PiercingItem[];
    bookingConfirmed: boolean;
  } | null>(null);

  // Multi-piercing state
  const [piercingCart, setPiercingCart] = useState<BookingPiercingItem[]>(() => {
    if (isFreshBookingStart()) return [];
    return readPersistedBookingDraft()?.piercingCart ?? [];
  });
  const [showAddAnotherModal, setShowAddAnotherModal] = useState(false);
  const [editingPiercingId, setEditingPiercingId] = useState<string | null>(null);
  const [isAddingAnotherPiercing, setIsAddingAnotherPiercing] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false); // Track if we're navigating back (data restored from cart)
  /** Hovered piercing type in the guide list (preview highlight on illustration). */
  const [previewPiercingTypeId, setPreviewPiercingTypeId] = useState('');

  // Temporarily disable the supplements step (keep code to re-enable later).
  const supplementsStepEnabled = false;
  useScrollOnStepChange(currentStep, bookingTopRef);

  const piercingProgressSteps = useMemo(() => {
    return supplementsStepEnabled
      ? [
          { key: 'piercingChoice', label: t('piercingBooking.steps.piercingChoice') },
          { key: 'supplements', label: t('piercingBooking.steps.supplements') },
          { key: 'contactDetails', label: t('piercingBooking.steps.contactDetails') },
          { key: 'appointmentChoice', label: t('piercingBooking.steps.appointmentChoice') },
          { key: 'payment', label: t('piercingBooking.steps.payment') },
          { key: 'confirmation', label: t('piercingBooking.steps.confirmation') },
        ]
      : [
          { key: 'piercingChoice', label: t('piercingBooking.steps.piercingChoice') },
          { key: 'contactDetails', label: t('piercingBooking.steps.contactDetails') },
          { key: 'appointmentChoice', label: t('piercingBooking.steps.appointmentChoice') },
          { key: 'payment', label: t('piercingBooking.steps.payment') },
          { key: 'confirmation', label: t('piercingBooking.steps.confirmation') },
        ];
  }, [supplementsStepEnabled, t]);

  const mapPiercingDisplayStep = (step: number): number => {
    if (step === 3) return 1;
    if (supplementsStepEnabled && step === 4) return 2;
    if (step === 1 || step === 2 || step === 6) {
      return supplementsStepEnabled ? 3 : 2;
    }
    if (step === 5) return supplementsStepEnabled ? 4 : 3;
    if (step === 7 || step === 8) return supplementsStepEnabled ? 5 : 4;
    if (step === 9) return supplementsStepEnabled ? 6 : 5;
    return 1;
  };

  const paymentMethodIcons: Record<PiercingPaymentMethod, typeof CreditCard> = {
    card: CreditCard,
    cash: User,
    bank_transfer: MapPin,
  };

  const isMinorFromDateOfBirth = (dateOfBirth?: string): boolean => {
    if (!dateOfBirth) return false;
    const normalizedDate = dateOfBirth.includes('T')
      ? dateOfBirth.split('T')[0]
      : dateOfBirth;
    const birthDate = new Date(`${normalizedDate}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age < 18;
  };
  useEffect(() => {
    if (!clientCreatedNotice) return;
    setShowClientCreatedToast(true);
    const timeout = window.setTimeout(() => {
      setShowClientCreatedToast(false);
    }, 6000);
    return () => window.clearTimeout(timeout);
  }, [clientCreatedNotice]);
  
  // Enhanced Planning slot state
  const [availableSlots, setAvailableSlots] = useState<Array<{ start: string; end: string; isAvailable: boolean; practitionerId?: string }>>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [piercingPlanningRecommendation, setPiercingPlanningRecommendation] = useState<string | null>(null);


  const [formData, setFormData] = useState<BookingFormData>(() => ({
    ...getDefaultBookingFormData(),
    ...(isFreshBookingStart() ? {} : readPersistedBookingDraft()?.formData ?? {}),
  }));
  const bookingClientIsMinor = formData.dateOfBirth
    ? isMinorFromDateOfBirth(formData.dateOfBirth)
    : formData.isMinor === true;

  useEffect(() => {
    if (systemStatusLoading || piercingBookingAnonymousEnabled || !formData.isAnonymous) return;
    setFormData((prev) => ({
      ...prev,
      clientId: undefined,
      isAnonymous: false,
      anonymousCode: undefined,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    }));
    setCurrentStep(1);
  }, [systemStatusLoading, piercingBookingAnonymousEnabled, formData.isAnonymous]);
  const selectedPaymentMethod = ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS.find(
    (method) => method.value === formData.paymentMethod
  ) || ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS[0];

  useEffect(() => {
    if (
      !ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS.some((method) => method.value === formData.paymentMethod) &&
      ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS[0]
    ) {
      setFormData(prev => ({ ...prev, paymentMethod: ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS[0].value }));
    }
  }, [formData.paymentMethod]);

  useEffect(() => {
    if (currentStep !== 5) return;
    setPiercingConflict(null);
    lastPiercingConflictKey.current = null;
  }, [currentStep, formData.availableDate, formData.shopId, formData.clientId]);

  useEffect(() => {
    if (currentStep !== 5) return;
    if (!formData.clientId || !formData.shopId || !formData.availableDate) return;

    const piercingsPayload = piercingCart.length > 0
      ? piercingCart.map(piercing => ({
          bodyZoneId: piercing.bodyZoneId,
          piercingTypeId: piercing.piercingTypeId,
          selectedSupplements: piercing.selectedSupplements || [],
        }))
      : [{
          bodyZoneId: formData.bodyZoneId,
          piercingTypeId: formData.piercingTypeId,
          selectedSupplements: formData.selectedSupplements || [],
        }];

    const checkKey = JSON.stringify({
      clientId: formData.clientId,
      shopId: formData.shopId,
      availableDate: formData.availableDate,
      selectedTimeSlot: formData.selectedTimeSlot,
      piercingsPayload,
    });

    if (lastPiercingConflictKey.current === checkKey) return;
    lastPiercingConflictKey.current = checkKey;

    let isMounted = true;
    const runCheck = async () => {
      try {
        setCheckingPiercingConflict(true);
        const payload: any = {
          clientId: formData.clientId,
          shopId: formData.shopId,
          availableDate: formData.availableDate,
          piercings: piercingsPayload,
        };

        if (formData.selectedTimeSlot) {
          payload.selectedTimeSlot = formData.selectedTimeSlot;
        }

        const data = await bookingApi.checkPiercingConflict(payload);

        if (!isMounted) return;

        if (data.success && data.data?.conflict) {
          const conflictReason = data.data.conflictReason
            || (data.data.conflictAppointment ? 'client_gap' : 'shop_unavailable');

          setPiercingConflict({
            minGapHours: data.data.minGapHours,
            conflictScope: data.data.conflictScope || (formData.selectedTimeSlot ? 'time' : 'date'),
            conflictReason,
            conflictAppointment: data.data.conflictAppointment,
          });

          if (formData.selectedTimeSlot) {
            setBookingConflictSelection({
              date: formData.availableDate,
              time: formData.selectedTimeSlot,
            });
            setBookingConflictMessage(
              conflictReason === 'client_gap'
                ? 'Ce client a déjà un rendez-vous sur ce créneau horaire.'
                : 'Ce créneau est déjà réservé dans ce shop.',
            );
          }

          setFormData(prev => ({ ...prev, selectedTimeSlot: '' }));
          lastPiercingConflictKey.current = null;
          return;
        }

        setPiercingConflict(null);
      } catch (error) {
        console.error('Piercing conflict check failed:', error);
      } finally {
        if (isMounted) setCheckingPiercingConflict(false);
      }
    };

    runCheck();
    return () => {
      isMounted = false;
    };
  }, [
    currentStep,
    formData.clientId,
    formData.shopId,
    formData.availableDate,
    formData.selectedTimeSlot,
    formData.bodyZoneId,
    formData.piercingTypeId,
    formData.selectedSupplements,
    piercingCart,
  ]);

  // Regression guard: rehydrate existing client from form data when navigating back
  useEffect(() => {
    if (existingClient || !formData.clientId) return;
    if (!formData.email && !formData.phone) return;

    const normalizedContactEmail = (contactInfo.email || '').trim().toLowerCase();
    const normalizedContactPhone = (contactInfo.phone || '').trim();
    const normalizedFormEmail = (formData.email || '').trim().toLowerCase();
    const normalizedFormPhone = (formData.phone || '').trim();

    if ((normalizedContactEmail && normalizedContactEmail !== normalizedFormEmail)
      || (normalizedContactPhone && normalizedContactPhone !== normalizedFormPhone)) {
      return;
    }

    setExistingClient({
      id: formData.clientId,
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      phone: formData.phone || '',
      dateOfBirth: formData.dateOfBirth || '',
      emergencyContact: formData.emergencyContact,
    });
  }, [
    existingClient,
    formData.clientId,
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phone,
    formData.dateOfBirth,
    formData.emergencyContact,
    contactInfo.email,
    contactInfo.phone,
  ]);

  // If the user is authenticated, prefill contact info from their account
  useEffect(() => {
    if (isAuthenticated && user) {
      setContactInfo({
        email: (user as any).email || '',
        phone: (user as any).phone || ''
      });
      setFormData(prev => ({
        ...prev,
        email: (user as any).email || prev.email,
        phone: (user as any).phone || prev.phone
      }));
      // No jump: when at step 1 and authenticated, we render the confirmation UI directly
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!shouldStartFresh) return;
    sessionStorage.removeItem(RESUME_STORAGE_KEY);
    localStorage.removeItem(CADDY_STORAGE_KEY);
    journeyIdRef.current = getOrCreatePiercingJourneyId(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('fresh');
    setSearchParams(nextParams, { replace: true });
  }, [shouldStartFresh, searchParams, setSearchParams]);

  useEffect(() => {
    bookingJourneyApi.ping({
      journeyId: journeyIdRef.current,
      stage: 'STARTED',
      eventType: 'ENTER_STAGE',
    });
  }, []);

  useEffect(() => {
    resumeStateRef.current = {
      currentStep,
      formData,
      contactInfo,
      piercingCart,
      bookingConfirmed
    };
  }, [currentStep, formData, contactInfo, piercingCart, bookingConfirmed]);

  const saveBookingDraft = () => {
    const resumeData = resumeStateRef.current;
    if (!resumeData) return;
    if (resumeData.bookingConfirmed || resumeData.currentStep >= 9) return;
    try {
      // Persist booking "caddy" so we can restore after login/navigation.
      const payload = JSON.stringify({
        currentStep: resumeData.currentStep,
        formData: resumeData.formData,
        contactInfo: resumeData.contactInfo,
        piercingCart: resumeData.piercingCart
      });
      sessionStorage.setItem(RESUME_STORAGE_KEY, payload);
      localStorage.setItem(CADDY_STORAGE_KEY, payload);
    } catch (error) {
      console.warn('Failed to save piercing booking session:', error);
    }
  };

  useEffect(() => {
    saveBookingDraft();
  }, [currentStep, formData, contactInfo, piercingCart, bookingConfirmed]);

  useEffect(() => {
    if (bookingConfirmed || currentStep >= 9) {
      sessionStorage.removeItem(RESUME_STORAGE_KEY);
      localStorage.removeItem(CADDY_STORAGE_KEY);
    }
  }, [bookingConfirmed, currentStep]);

  useEffect(() => {
    return () => {
      const resumeData = resumeStateRef.current;
      if (!resumeData) return;
      if (resumeData.bookingConfirmed || resumeData.currentStep >= 9) return;
      try {
        const payload = JSON.stringify({
          currentStep: resumeData.currentStep,
          formData: resumeData.formData,
          contactInfo: resumeData.contactInfo,
          piercingCart: resumeData.piercingCart
        });
        sessionStorage.setItem(RESUME_STORAGE_KEY, payload);
        localStorage.setItem(CADDY_STORAGE_KEY, payload);
      } catch (error) {
        console.warn('Failed to save piercing booking session:', error);
      }
    };
  }, []);

  useEffect(() => {
    if (currentStep >= 9) return;
    const cartCount = piercingCart.length > 0 ? piercingCart.length : (formData.piercingTypeId ? 1 : 0);
    const stage = mapPiercingStepToJourneyStage(currentStep, cartCount);
    const timer = window.setTimeout(() => {
      bookingJourneyApi.ping({
        journeyId: journeyIdRef.current,
        stage,
        eventType: currentStep === 3 || currentStep === 4 ? (cartCount > 0 ? 'ADD_TO_CART' : 'ENTER_STAGE') : 'ENTER_STAGE',
        shopId: formData.shopId || undefined,
        email: formData.email || contactInfo.email || undefined,
        phone: formData.phone || contactInfo.phone || undefined,
        anonymousCode: formData.anonymousCode || undefined,
        snapshot: {
          cart: (piercingCart.length > 0 ? piercingCart : formData.piercingTypeId ? [{
            bodyZoneId: formData.bodyZoneId,
            piercingTypeId: formData.piercingTypeId,
            selectedSupplements: formData.selectedSupplements,
          }] : []).map((p) => ({
            bodyZoneId: p.bodyZoneId,
            piercingTypeId: p.piercingTypeId,
            bodyZoneName: 'bodyZoneName' in p ? p.bodyZoneName : undefined,
            piercingTypeName: 'piercingTypeName' in p ? p.piercingTypeName : undefined,
            selectedSupplements: p.selectedSupplements,
          })),
          availableDate: formData.availableDate || undefined,
          selectedTimeSlot: formData.selectedTimeSlot || undefined,
        },
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    currentStep,
    piercingCart,
    formData.shopId,
    formData.clientId,
    formData.email,
    formData.phone,
    formData.anonymousCode,
    formData.availableDate,
    formData.selectedTimeSlot,
    formData.bodyZoneId,
    formData.piercingTypeId,
    formData.selectedSupplements,
    contactInfo.email,
    contactInfo.phone,
  ]);

  useEffect(() => {
    if (currentStep >= 9) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const cartCount = piercingCart.length > 0 ? piercingCart.length : (formData.piercingTypeId ? 1 : 0);
      bookingJourneyApi.ping({
        journeyId: journeyIdRef.current,
        stage: mapPiercingStepToJourneyStage(currentStep, cartCount),
        eventType: 'HEARTBEAT',
        shopId: formData.shopId || undefined,
      });
    };
    const interval = window.setInterval(tick, 45_000);
    const onHide = () => {
      const cartCount = piercingCart.length > 0 ? piercingCart.length : (formData.piercingTypeId ? 1 : 0);
      bookingJourneyApi.beacon({
        journeyId: journeyIdRef.current,
        stage: mapPiercingStepToJourneyStage(currentStep, cartCount),
        eventType: 'PAGE_HIDDEN',
        shopId: formData.shopId || undefined,
      });
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('pagehide', onHide);
    };
  }, [currentStep, piercingCart.length, formData.shopId, formData.piercingTypeId]);

  // Removed unused steps array

  useEffect(() => {
    fetchBodyZones();
  }, []);

  useEffect(() => {
    if (bodyZones.length === 0) return;
    let cancelled = false;
    Promise.all(
      bodyZones.map(async (zone) => {
        try {
          const data = await catalogApi.fetchPiercingTypes(zone.id);
          const types = data.success ? data.data.piercingTypes : [];
          const onlyForbidden =
            types.length > 0 && types.every((type) => type.ageRestriction >= 18);
          return onlyForbidden ? zone.id : null;
        } catch {
          return null;
        }
      }),
    ).then((ids) => {
      if (cancelled) return;
      setMinorOnlyZoneIds(new Set(ids.filter((id): id is string => id !== null)));
    });
    return () => {
      cancelled = true;
    };
  }, [bodyZones]);

  useEffect(() => {
    if (formData.bodyZoneId) {
      fetchPiercingTypes(formData.bodyZoneId);
    }
  }, [formData.bodyZoneId]);

  useEffect(() => {
    if (formData.piercingTypeId) {
      fetchShops();
    }
  }, [formData.piercingTypeId]);

  // Fetch shops when entering shop selection step if we have piercings in cart
  useEffect(() => {
    if (currentStep === 5 && piercingCart.length > 0) {
      // Use the first piercing's type to fetch shops if we don't have shops yet
      const firstPiercing = piercingCart[0];
      if (firstPiercing?.piercingTypeId && shops.length === 0) {
        fetchShopsForPiercingType(firstPiercing.piercingTypeId);
      }
    }
  }, [currentStep, piercingCart]);

  useEffect(() => {
    if (currentStep !== 5 || !formData.shopId) {
      setPiercingPlanningRecommendation(null);
      return;
    }

    let cancelled = false;
    const loadPiercingRecommendation = async () => {
      try {
        const response = await onlineBookingApi.getServices(formData.shopId, 'PIERCING');
        if (cancelled) return;
        const withRecommendation = (response.data.services || []).find(
          (service) => hasVisibleHtmlContent(service.planningRecommendation),
        );
        const recommendation = withRecommendation?.planningRecommendation;
        setPiercingPlanningRecommendation(
          hasVisibleHtmlContent(recommendation) ? recommendation! : null,
        );
      } catch (error) {
        if (!cancelled) {
          setPiercingPlanningRecommendation(null);
        }
      }
    };

    loadPiercingRecommendation();
    return () => {
      cancelled = true;
    };
  }, [currentStep, formData.shopId]);

  // Recalculate pricing when piercing cart changes
  useEffect(() => {
    calculatePricing();
  }, [
    piercingCart,
    piercingTypes,
    formData.piercingTypeId,
    formData.selectedSupplements,
  ]);

  // Clear stale "add a piercing" banner once a selection exists
  useEffect(() => {
    if (piercingCart.length === 0) return;
    setError((prev) => {
      if (!prev) return prev;
      const addMsg = t('piercingBooking.errors.addAtLeastOne');
      if (prev === addMsg || /au moins un piercing|at least one piercing/i.test(prev)) {
        return null;
      }
      return prev;
    });
  }, [piercingCart, t]);

  // Ensure all piercing types for cart items are loaded
  useEffect(() => {
    const loadMissingTypes = async () => {
      if (piercingCart.length === 0) return;
      
      // Get unique body zone IDs from cart
      const cartBodyZoneIds = [...new Set(piercingCart.map(p => p.bodyZoneId).filter(Boolean))];
      
      // For each body zone in cart, ensure we have its types loaded
      for (const bodyZoneId of cartBodyZoneIds) {
        // Check if we already have types for this body zone
        const hasTypesForZone = piercingTypes.some(t => t.bodyZoneId === bodyZoneId);
        
        if (!hasTypesForZone) {
          // Fetch types for this body zone
          try {
            const data = await catalogApi.fetchPiercingTypes(bodyZoneId);
            if (data.success) {
              setPiercingTypes(prev => {
                const existingIds = new Set(prev.map(t => t.id));
                const newTypes = data.data.piercingTypes.filter(t => !existingIds.has(t.id));
                return [...prev, ...newTypes];
              });
            }
          } catch (error) {
            console.error(`Failed to fetch types for body zone ${bodyZoneId}:`, error);
          }
        }
      }
    };
    
    loadMissingTypes();
  }, [piercingCart]);

  // Fetch available slots when shop and date are selected
  useEffect(() => {
    if (currentStep !== 5) return;
    if (formData.shopId && formData.availableDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [currentStep, formData.shopId, formData.availableDate, formData.dateOfBirth, formData.piercingTypeId, piercingCart, piercingTypes]);

  // Refresh slot list before payment in case availability changed since selection
  useEffect(() => {
    if (currentStep !== 7) return;
    if (formData.shopId && formData.availableDate) {
      fetchAvailableSlots();
    }
  }, [currentStep, formData.shopId, formData.availableDate, formData.dateOfBirth, formData.piercingTypeId, piercingCart, piercingTypes]);

  // Cart management functions — returns the updated cart synchronously for callers that need it.
  const addPiercingToCart = (forceAdd: boolean = false): PiercingItem[] | null => {
    if (!formData.bodyZoneId || !formData.piercingTypeId) {
      setError(t('piercingBooking.errors.selectPiercingFirst') || 'Veuillez d’abord sélectionner un piercing');
      return null;
    }

    // Ensure selectedSupplements is always an array
    const supplementsToAdd = Array.isArray(formData.selectedSupplements) 
      ? [...formData.selectedSupplements] 
      : [];

    const trimmedNoteToPractitioner = htmlToText(formData.noteToPractitioner || '').trim()
      ? formData.noteToPractitioner
      : '';

    console.log('Adding piercing to cart with supplements:', supplementsToAdd);

    // Helper function to compare supplements arrays
    const supplementsEqual = (arr1: string[], arr2: string[]): boolean => {
      if (arr1.length !== arr2.length) return false;
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();
      return sorted1.every((val, idx) => val === sorted2[idx]);
    };

    const duplicateBodyZoneInCart = piercingCart.some(p =>
      p.bodyZoneId === formData.bodyZoneId && p.id !== editingPiercingId
    );

    if (PIERCING_COMBINATION_RULES_ENABLED && duplicateBodyZoneInCart) {
      setError('Cette zone du corps est déjà sélectionnée. Choisissez une autre zone.');
      return null;
    }

    // Check if this exact piercing configuration already exists in the cart
    const existingPiercing = piercingCart.find(p => 
      p.bodyZoneId === formData.bodyZoneId &&
      p.piercingTypeId === formData.piercingTypeId &&
      supplementsEqual(
        Array.isArray(p.selectedSupplements) ? p.selectedSupplements : [],
        supplementsToAdd
      )
    );

    // If we're editing, use the editing logic
    if (editingPiercingId) {
      const selectedPiercingType = piercingTypes.find(p => p.id === formData.piercingTypeId);
      const selectedBodyZone = bodyZones.find(zone => zone.id === formData.bodyZoneId);
      const newPiercing: BookingPiercingItem = {
        id: editingPiercingId,
        bodyZoneId: formData.bodyZoneId,
        piercingTypeId: formData.piercingTypeId,
        selectedSupplements: supplementsToAdd,
        noteToPractitioner: trimmedNoteToPractitioner || undefined,
        piercingTypeName: selectedPiercingType?.name,
        bodyZoneName: selectedBodyZone?.name,
        basePrice: selectedPiercingType ? Number(selectedPiercingType.basePrice) || 0 : undefined
      };
      const updatedCart = piercingCart.map(p => p.id === editingPiercingId ? newPiercing : p);
      setPiercingCart(updatedCart);
      
      // Update formData.piercings for backend compatibility
      setFormData(prev => ({
        ...prev,
        piercings: updatedCart
      }));

      // Recalculate pricing
      let totalPrice = 0;
      updatedCart.forEach((piercing) => {
        const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
        if (!piercingType) return;

        const basePrice = piercingType.basePrice;
        const supplementsArray = Array.isArray(piercing.selectedSupplements) 
          ? piercing.selectedSupplements 
          : [];
        const supplementsTotal = supplementsArray.reduce((suppTotal: number, suppId: string) => {
          const supplement = supplements.find(s => s.id === suppId);
          return suppTotal + (supplement?.price || 0);
        }, 0);

        totalPrice += basePrice + supplementsTotal;
      });

      const depositAmount = PIERCING_DEPOSIT_AMOUNT_EUR;
      setFormData(prev => ({
        ...prev,
        totalPrice,
        depositAmount
      }));

      return updatedCart;
    }

    // If exact same piercing already exists in cart, don't add it again
    if (PIERCING_COMBINATION_RULES_ENABLED && existingPiercing) {
      console.log('Piercing already in cart, skipping duplicate add');
      setEditingPiercingId(existingPiercing.id);
      return piercingCart;
    }

    const selectedPiercingType = piercingTypes.find(p => p.id === formData.piercingTypeId);
    const selectedBodyZone = bodyZones.find(zone => zone.id === formData.bodyZoneId);
    const newPiercing: BookingPiercingItem = {
      id: `piercing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      bodyZoneId: formData.bodyZoneId,
      piercingTypeId: formData.piercingTypeId,
      selectedSupplements: supplementsToAdd,
      noteToPractitioner: trimmedNoteToPractitioner || undefined,
      piercingTypeName: selectedPiercingType?.name,
      bodyZoneName: selectedBodyZone?.name,
      basePrice: selectedPiercingType ? Number(selectedPiercingType.basePrice) || 0 : undefined
    };

    // Calculate the updated cart for immediate pricing calculation
    const updatedCart = [...piercingCart, newPiercing];
    
    // Update cart state
    setPiercingCart(updatedCart);
    setEditingPiercingId(newPiercing.id);

    // Update formData.piercings for backend compatibility
    setFormData(prev => ({
      ...prev,
      piercings: updatedCart
    }));

    // Explicitly recalculate pricing with the updated cart
    let totalPrice = 0;
    updatedCart.forEach((piercing) => {
      const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
      if (!piercingType) return;

      const basePrice = piercingType.basePrice;
      const supplementsArray = Array.isArray(piercing.selectedSupplements) 
        ? piercing.selectedSupplements 
        : [];
      const supplementsTotal = supplementsArray.reduce((suppTotal: number, suppId: string) => {
        const supplement = supplements.find(s => s.id === suppId);
        return suppTotal + (supplement?.price || 0);
      }, 0);

      totalPrice += basePrice + supplementsTotal;
    });

    const depositAmount = PIERCING_DEPOSIT_AMOUNT_EUR;
    setFormData(prev => ({
      ...prev,
      totalPrice,
      depositAmount
    }));

    return updatedCart;
  };

  const resetPiercingSelectionForm = () => {
    setEditingPiercingId(null);
    setIsNavigatingBack(false);
    setIsAddingAnotherPiercing(false);
    setFormData(prev => ({
      ...prev,
      bodyZoneId: '',
      piercingTypeId: '',
      selectedSupplements: [],
      noteToPractitioner: '',
      piercings: [],
      totalPrice: 0,
      depositAmount: 0,
    }));
  };

  const removePiercingFromCart = (piercingId: string) => {
    const updatedCart = piercingCart.filter(p => p.id !== piercingId);
    setPiercingCart(updatedCart);
    setFormData(prev => ({
      ...prev,
      piercings: updatedCart
    }));
    
    // Recalculate pricing immediately after removal
    let totalPrice = 0;
    updatedCart.forEach((piercing) => {
      const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
      if (!piercingType) return;

      const basePrice = piercingType.basePrice;
      const supplementsArray = Array.isArray(piercing.selectedSupplements) 
        ? piercing.selectedSupplements 
        : [];
      const supplementsTotal = supplementsArray.reduce((suppTotal: number, suppId: string) => {
        const supplement = supplements.find(s => s.id === suppId);
        return suppTotal + (supplement?.price || 0);
      }, 0);

      totalPrice += basePrice + supplementsTotal;
    });

    const depositAmount = PIERCING_DEPOSIT_AMOUNT_EUR;
    setFormData(prev => ({
      ...prev,
      totalPrice,
      depositAmount
    }));

    if (updatedCart.length === 0) {
      resetPiercingSelectionForm();
    }
  };

  // editPiercingInCart function removed as it's not currently used

  const handleAddAnotherPiercing = () => {
    setShowAddAnotherModal(false);
    setIsAddingAnotherPiercing(true); // Set flag to allow duplicates when adding another
    setIsNavigatingBack(false); // Reset navigation flag since this is explicit add
    setEditingPiercingId(null); // Clear editing ID for new piercing
    setCurrentStep(3); // Go back to piercing selection
    // Reset form for new piercing
    setFormData(prev => ({
      ...prev,
      bodyZoneId: '',
      piercingTypeId: '',
      selectedSupplements: [],
      noteToPractitioner: ''
    }));
  };

  const handleCancelAdditionalPiercing = () => {
    setIsAddingAnotherPiercing(false);
    setIsNavigatingBack(false);
    setEditingPiercingId(null);
    setShowAddAnotherModal(true);
  };

  const restorePiercingSelectionFromCart = () => {
    if (piercingCart.length === 0) return;
    const lastPiercing = piercingCart[piercingCart.length - 1];
    setFormData(prev => ({
      ...prev,
      bodyZoneId: lastPiercing.bodyZoneId || prev.bodyZoneId,
      piercingTypeId: lastPiercing.piercingTypeId || prev.piercingTypeId,
      selectedSupplements: Array.isArray(lastPiercing.selectedSupplements)
        ? [...lastPiercing.selectedSupplements]
        : [],
      noteToPractitioner: lastPiercing.noteToPractitioner || ''
    }));
    setIsNavigatingBack(true); // Mark that we're navigating back (data restored from cart)
    setEditingPiercingId(lastPiercing.id);
  };

  const handleCompleteBooking = () => {
    setShowAddAnotherModal(false);
    setIsAddingAnotherPiercing(false);
    // Use latest selection: state may still be empty if user confirms in the same tick as add.
    const selectionCount =
      piercingCart.length ||
      (Array.isArray(formData.piercings) ? formData.piercings.length : 0);

    if (selectionCount === 0) {
      // Last chance: current form selection not yet committed to cart
      if (formData.bodyZoneId && formData.piercingTypeId) {
        const updated = addPiercingToCart(false);
        if (!updated || updated.length === 0) {
          setError(t('piercingBooking.errors.addAtLeastOne'));
          return;
        }
      } else {
        setError(t('piercingBooking.errors.addAtLeastOne'));
        return;
      }
    }

    setError(null);
    setCurrentStep(1); // Go to contact details before appointment selection
    scrollBookingIntoView(bookingTopRef.current);
  };

  // Removed debouncedCheckClient function as we now check client existence in validation

  // Handle contact info changes
  const handleContactChange = (field: 'email' | 'phone', value: string) => {
    const newContactInfo = { ...contactInfo, [field]: value };
    setContactInfo(newContactInfo);

    setFieldErrors((prev) => {
      if (!prev[field] && !prev.contact) return prev;
      const next = { ...prev };
      delete next[field];
      delete next.contact;
      return next;
    });
    
    // Clear any existing client confirmation when user changes contact info
    if (existingClientFound) {
      setExistingClientFound(null);
      setShowClientConfirmation(false);
    }

    if (existingClient) {
      const normalizedValue = value.trim().toLowerCase();
      const existingEmail = (existingClient.email || '').trim().toLowerCase();
      const existingPhone = (existingClient.phone || '').trim();
      const mismatch = field === 'email'
        ? normalizedValue !== existingEmail
        : value.trim() !== existingPhone;

      if (mismatch) {
        setExistingClient(null);
        setOtpSent(false);
        setContactLookupStatus('unknown');
        setFormData(prev => ({
          ...prev,
          clientId: undefined,
          email: field === 'email' ? value : prev.email,
          phone: field === 'phone' ? value : prev.phone,
        }));
      }
    }
  };

  const handleUseExistingClient = async () => {
    if (!existingClientFound) return;
    
    try {
      setLoading(true);
      setResendLimitReached(false);
      setError('');

      // NOW send the OTP — user has explicitly confirmed they want to use the existing account
      const otpResp = await clientApi.sendOtp({ clientId: existingClientFound.id });

      if (!otpResp.success) {
        setError(otpResp.message || 'Erreur lors de l\'envoi du code de vérification');
        return;
      }

      if (otpResp.otpChannel) setOtpChannel(otpResp.otpChannel);
      if (otpResp.attemptsLeft != null) setOtpAttemptsLeft(otpResp.attemptsLeft);
      if (otpResp.destinationHint) setOtpDestinationHint(otpResp.destinationHint);

      setOtpSent(true);
      setShowClientConfirmation(false);
      setCurrentStep(2); // Navigate to OTP verification step
      
      // Scroll to top
      scrollBookingIntoView(bookingTopRef.current);
      
      const channelLabel = (otpResp.otpChannel || 'email') === 'sms' ? 'SMS' : 'email';
      const dest = otpResp.destinationHint || '';
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
      successMessage.textContent = dest
        ? `Code de vérification envoyé par ${channelLabel} (${dest})`
        : `Code de vérification envoyé par ${channelLabel}`;
      document.body.appendChild(successMessage);
      
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 5000);
      
    } catch (error) {
      console.error('Error sending OTP:', error);
      const err = error as { status?: number; data?: { resendLimitReached?: boolean; message?: string } };
      if (err.status === 429) {
        setResendLimitReached(Boolean(err.data?.resendLimitReached));
        setError(err.data?.message || 'Trop de codes demandés. Réessayez plus tard.');
      } else {
        setError('Erreur lors de l\'envoi du code de vérification');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismissClientConfirmation = () => {
    setShowClientConfirmation(false);
    setExistingClientFound(null);
    setContactLookupStatus('unknown');
  };

  const handleCreateNewClient = () => {
    setExistingClientFound(null);
    setShowClientConfirmation(false);
    setOtpSent(false);
    setContactLookupStatus('create_new');
    // Keep email/phone so we can prefill account or guest details
    setFormData(prev => ({ ...prev, email: contactInfo.email || prev.email, phone: contactInfo.phone || prev.phone }));
    setCurrentStep(6);
    scrollBookingIntoView(bookingTopRef.current);
  };

  const handleOtpVerification = async () => {
    if (!otpCode.trim()) {
      setFieldErrors({ otpCode: 'Code de vérification requis' });
      return;
    }
    
    try {
      setLoading(true);
      
      const data = await clientApi.verifyOtp({ clientId: existingClientFound.id, otp: otpCode });
      
      if (data.success) {
        // Pre-fill form with existing client data
        setFormData(prev => ({
          ...prev,
          firstName: data.client.firstName,
          lastName: data.client.lastName,
          email: data.client.email,
          phone: data.client.phone,
          dateOfBirth: data.client.dateOfBirth ? data.client.dateOfBirth.split('T')[0] : '',
          emergencyContact: data.client.emergencyContact || {
            name: '',
            phone: '',
            relationship: ''
          }
        }));
        
        setExistingClient(data.client);
        setOtpVerified(true);
      } else {
        // Failed attempt
        if (typeof data.attemptsLeft === 'number') {
          setOtpAttemptsLeft(data.attemptsLeft);
        }
        if (data.requireResend) {
          setFieldErrors({ otpCode: 'Nombre maximum d\'essais atteint. Demandez un nouveau code.' });
        } else {
          setFieldErrors({ otpCode: 'Code de vérification invalide' });
        }
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setFieldErrors({ otpCode: 'Erreur lors de la vérification' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await clientApi.resendOtp({ clientId: existingClientFound.id });
      setOtpCode('');
      setOtpAttemptsLeft(resp.attemptsLeft ?? null);
      if (resp.otpChannel === 'sms' || resp.otpChannel === 'email') setOtpChannel(resp.otpChannel);
      if (resp.destinationHint) setOtpDestinationHint(resp.destinationHint);
      setResendLimitReached(false);
      setFieldErrors({});
      const channelLabel = (resp.otpChannel || otpChannel) === 'sms' ? 'SMS' : 'email';
      const info = document.createElement('div');
      info.className = 'fixed top-4 right-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded z-50';
      info.textContent = `Nouveau code envoyé par ${channelLabel}`;
      document.body.appendChild(info);
      setTimeout(() => {
        if (document.body.contains(info)) document.body.removeChild(info);
      }, 3000);
    } catch (e: unknown) {
      console.error('Resend OTP error:', e);
      const err = e as { status?: number; data?: { resendLimitReached?: boolean; message?: string } };
      if (err.status === 429 && err.data?.resendLimitReached) {
        setResendLimitReached(true);
        setError(err.data?.message || t('piercingBooking.otp.resendLimitReached'));
      } else {
        setError((err.data as { message?: string })?.message || 'Impossible d\'envoyer un nouveau code');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBodyZones = async () => {
    try {
      setLoading(true);
      const data = await catalogApi.fetchBodyZones();
      
      if (data.success) {
        setBodyZones(data.data.bodyZones);
      }
    } catch (error) {
      console.error('Failed to fetch body zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPiercingTypes = async (bodyZoneId: string) => {
    try {
      setLoading(true);
      const data = await catalogApi.fetchPiercingTypes(bodyZoneId);
      
      if (data.success) {
        // Merge new types with existing ones to preserve types from other body zones
        setPiercingTypes(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTypes = data.data.piercingTypes.filter(t => !existingIds.has(t.id));
          return [...prev, ...newTypes];
        });
      }
    } catch (error) {
      console.error('Failed to fetch piercing types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShops = async () => {
    const piercingTypeId = formData.piercingTypeId || (piercingCart.length > 0 ? piercingCart[0]?.piercingTypeId : null);
    if (!piercingTypeId) return;
    
    try {
      setLoading(true);
      const data = await catalogApi.fetchShops(piercingTypeId);
      
      if (data.success) {
        setShops(data.data.shops);
      } else {
        setShops([]);
      }
    } catch (error) {
      console.error('Failed to fetch shops:', error);
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchShopsForPiercingType = async (piercingTypeId: string) => {
    try {
      setLoading(true);
      const data = await catalogApi.fetchShops(piercingTypeId);
      
      if (data.success) {
        setShops(data.data.shops);
      } else {
        setShops([]);
      }
    } catch (error) {
      console.error('Failed to fetch shops:', error);
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!formData.shopId || !formData.availableDate) return;
    
    try {
      setLoadingSlots(true);
      
      // Calculate service duration from piercing cart or current selection.
      // Use cart/form piercingTypeId directly so slots reload after a page refresh
      // before catalog piercing types have been re-fetched.
      const selectedPiercingTypes = piercingCart.length > 0
        ? piercingCart
            .filter((piercing) => Boolean(piercing.piercingTypeId))
            .map((piercing) => ({
              typeId: piercing.piercingTypeId,
              estimatedDuration:
                piercingTypes.find((type) => type.id === piercing.piercingTypeId)?.estimatedDuration ?? 30,
            }))
        : formData.piercingTypeId
          ? [{
              typeId: formData.piercingTypeId,
              estimatedDuration:
                piercingTypes.find((type) => type.id === formData.piercingTypeId)?.estimatedDuration ?? 30,
            }]
          : [];

      if (selectedPiercingTypes.length === 0) {
        setAvailableSlots([]);
        setLoadingSlots(false);
        return;
      }

      const piercingCount = piercingCart.length > 0
        ? piercingCart.length
        : formData.piercingTypeId
          ? 1
          : 0;
      
      // Request the full day; the backend slot engine filters
      // based on actual shop opening hours and practitioner availability.
      const from = new Date(formData.availableDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(formData.availableDate);
      to.setHours(23, 59, 59, 999);

      const response = await PlanningApi.getSlots({
        shopId: formData.shopId,
        from: from.toISOString(),
        to: to.toISOString(),
        piercingTypeId: selectedPiercingTypes[0]?.typeId,
        piercingTypeIds: selectedPiercingTypes.map((piercing) => piercing.typeId),
        minor: bookingClientIsMinor,
      });

      if (response.success) {
        console.log(`📅 Fetched ${response.data.slots.length} total slots for shop ${formData.shopId} on ${formData.availableDate}`);
        
        // Calculate how many consecutive slots we need
        const slotSizeMinutes = response.data.slotSizeMinutes || 30;
        const additionalActMinutes = response.data.slotSizeAdditionalActMinutes || slotSizeMinutes;
        const effectiveAdditionalActMinutes = bookingClientIsMinor
          ? Math.max(additionalActMinutes, slotSizeMinutes)
          : additionalActMinutes;
        const fallbackDurationMinutes =
          slotSizeMinutes + Math.max(0, piercingCount - 1) * effectiveAdditionalActMinutes;
        const ruleBasedDurationMinutes =
          response.data.requiredDurationMinutes || fallbackDurationMinutes;
        const requiredSlotsByDuration = Math.ceil(ruleBasedDurationMinutes / slotSizeMinutes);
        const requiredSlots = Math.max(1, requiredSlotsByDuration);
        console.log(`⏱️ Rule-based duration required: ${ruleBasedDurationMinutes} minutes`);
        console.log(`📊 Need ${requiredSlots} consecutive slots (slot size: ${slotSizeMinutes} min)`);
        
        // Sort slots by practitioner and time
        const sortedSlots = [...response.data.slots].sort((a, b) => {
          if (a.practitionerId !== b.practitionerId) {
            return (a.practitionerId || '').localeCompare(b.practitionerId || '');
          }
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        });
        
        // Filter slots: keep only slots where there are enough consecutive available slots
        const filteredSlots: Array<{ start: string; end: string; isAvailable: boolean; practitionerId?: string }> = [];
        
        for (const slot of sortedSlots) {
          if (!slot.isAvailable) continue;
                    
          // If service fits in one slot, just add it
          if (requiredSlots === 1) {
            filteredSlots.push({
              start: slot.start,
              end: slot.end,
              isAvailable: slot.isAvailable,
              practitionerId: slot.practitionerId
            });
            continue;
          }
          
          // For multi-slot services, check if there are enough consecutive slots
          const slotStartTime = new Date(slot.start).getTime();
          const slotEndTime = new Date(slot.end).getTime();
          const slotDuration = slotEndTime - slotStartTime;
          
          // Find consecutive slots starting from this slot
          let consecutiveSlots = [slot];
          let currentEndTime = slotEndTime;
          
          for (let i = 1; i < requiredSlots; i++) {
            const nextSlot = sortedSlots.find(s => {
              if (!s.isAvailable || (s.practitionerId || null) !== (slot.practitionerId || null)) return false;
              const sStart = new Date(s.start).getTime();
              // Check if this slot starts right after the previous one (within 1 second tolerance)
              return Math.abs(sStart - currentEndTime) < 1000;
            });
            
            if (nextSlot) {
              consecutiveSlots.push(nextSlot);
              currentEndTime = new Date(nextSlot.end).getTime();
            } else {
              break;
            }
          }
          
          // If we found enough consecutive slots, add the first one
          if (consecutiveSlots.length >= requiredSlots) {
            filteredSlots.push({
              start: slot.start,
              end: slot.end,
              isAvailable: slot.isAvailable,
              practitionerId: slot.practitionerId
            });
          }
        }
        
        console.log(`✅ Found ${filteredSlots.length} available slots after filtering (need ${requiredSlots} consecutive)`);
        
        // Deduplicate slots by start time (multiple practitioners can have slots at the same time)
        // Use a Map to keep only the first occurrence of each unique start time, but preserve practitionerId
        const uniqueSlots = new Map<string, { start: string; end: string; isAvailable: boolean; practitionerId?: string }>();
        filteredSlots.forEach(slot => {
          const timeKey = extractSlotLocalTime(slot.start);
          if (!uniqueSlots.has(timeKey)) {
            uniqueSlots.set(timeKey, slot);
          }
        });
        
        // Convert back to array and sort by time
        const deduplicatedSlots = Array.from(uniqueSlots.values()).sort((a, b) => 
          new Date(a.start).getTime() - new Date(b.start).getTime()
        );
        
        console.log(`🎯 Deduplicated to ${deduplicatedSlots.length} unique time slots`);
        setAvailableSlots(filterPastSlotsForToday(deduplicatedSlots, formData.availableDate));
      } else {
        console.error('❌ Failed to fetch slots: response was not successful');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

const calculatePricing = () => {
  let totalPrice = 0;

  if (piercingCart.length > 0) {
    totalPrice = piercingCart.reduce(
      (total, piercing) => {
        const piercingType =
          piercingTypes.find(
            (type) =>
              type.id ===
              piercing.piercingTypeId
          );

        const basePrice =
          Number(
            piercingType?.basePrice ?? piercing.basePrice ?? 0
          ) || 0;

        const supplementsArray =
          Array.isArray(
            piercing.selectedSupplements
          )
            ? piercing.selectedSupplements
            : [];

        const supplementsTotal =
          supplementsArray.reduce(
            (
              supplementTotal,
              supplementId
            ) => {
              const supplement =
                supplements.find(
                  (item) =>
                    item.id ===
                    supplementId
                );

              return (
                supplementTotal +
                (Number(
                  supplement?.price
                ) || 0)
              );
            },
            0
          );

        return (
          total +
          basePrice +
          supplementsTotal
        );
      },
      0
    );
  } else if (
    formData.piercingTypeId
  ) {
    const selectedPiercing =
      piercingTypes.find(
        (piercing) =>
          piercing.id ===
          formData.piercingTypeId
      );

    if (selectedPiercing) {
      const basePrice =
        Number(
          selectedPiercing.basePrice
        ) || 0;

      const supplementsArray =
        Array.isArray(
          formData.selectedSupplements
        )
          ? formData.selectedSupplements
          : [];

      const supplementsTotal =
        supplementsArray.reduce(
          (
            total,
            supplementId
          ) => {
            const supplement =
              supplements.find(
                (item) =>
                  item.id ===
                  supplementId
              );

            return (
              total +
              (Number(
                supplement?.price
              ) || 0)
            );
          },
          0
        );

      totalPrice =
        basePrice +
        supplementsTotal;
    }
  }

  const depositAmount =
    totalPrice > 0
      ? PIERCING_DEPOSIT_AMOUNT_EUR
      : 0;

  console.log(
    '💰 Pricing recalculated:',
    {
      totalPrice,
      depositAmount,
      piercingCart,
      piercingTypesCount:
        piercingTypes.length,
    }
  );

  setFormData((prev) => {
    /*
     * Évite un setState inutile.
     */
    if (
      prev.totalPrice ===
        totalPrice &&
      prev.depositAmount ===
        depositAmount
    ) {
      return prev;
    }

    return {
      ...prev,
      totalPrice,
      depositAmount,
    };
  });
};

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });

    // If user changes piercing-related fields, reset navigation flag (they're editing, not just navigating)
    if (field === 'bodyZoneId' || field === 'piercingTypeId' || field === 'selectedSupplements') {
      setIsNavigatingBack(false);
    }

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (field === 'shopId' || field === 'availableDate' || field === 'selectedTimeSlot') {
      setPiercingConflict(null);
      lastPiercingConflictKey.current = null;
    }
  };

  // Removed unused legacy client existence/OTP helpers

  const validateStep = async (step: number): Promise<boolean> => {
    const newErrors: Record<string, string> = {};
    setFieldErrors({});

    switch (step) {
      case 1:
        // When authenticated, step 1 shows confirmation UI - skip email/phone validation
        if (isAuthenticated && user) {
          return true;
        }
        if (formData.isAnonymous && formData.clientId) {
          return true;
        }
        const contactEmail = contactInfo.email.trim();
        const contactPhone = contactInfo.phone.trim();
        const hasEmail = contactEmail.length > 0;
        const hasPhone = contactPhone.length > 0;

        if (hasEmail) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(contactEmail)) {
            newErrors.email = 'Format d\'email invalide';
          }
        }
        
        if (hasPhone) {
          if (!contactPhoneIsValid || !contactPhoneE164) {
            newErrors.phone =
              'Le numéro de téléphone est invalide pour le pays sélectionné';
          }
        }

      // If validation passed, check for existing client (only if not already decided)
      if (Object.keys(newErrors).length === 0) {
        if (!hasEmail && !hasPhone) {
          setContactLookupStatus('not_found');
          setExistingClient(null);
          setFormData(prev => ({ ...prev, clientId: undefined }));
          return true;
        }
        // Skip client check if user already made a decision (OTP sent or already processing existing client)
        if (otpSent || (existingClientFound && !showClientConfirmation)) {
          // User has already chosen to use existing account or is in OTP flow
          return true;
        }
        
        try {
          const data = await clientApi.checkClient({ 
            email: hasEmail ? contactEmail : '',
            phone: hasPhone ? contactPhoneE164 ?? '' : '',
          });
          
          console.log('Client check response:', { success: data.success, clientExists: data.clientExists });
          
          if (data.success && data.clientExists) {
            console.log('Existing client found:', data.client);
            setExistingClientFound(data.client);
            setContactLookupStatus('found');
            // Show confirmation modal for user to decide — OTP is sent later
            setShowClientConfirmation(true);
            return false; // Don't proceed, wait for user decision
          } else {
            console.log('No existing client found, proceeding to contact details');
            setExistingClientFound(null);
            setShowClientConfirmation(false);
            setContactLookupStatus('not_found');
            setExistingClient(null);
            setFormData(prev => ({ ...prev, clientId: undefined }));
            return true; // Allow progression to contact details
          }
        } catch (error) {
          console.error('Error checking client:', error);
          newErrors.contact = 'Erreur lors de la vérification du client';
        }
      }
        break;

      case 3:
        if (!formData.bodyZoneId) {
          newErrors.bodyZoneId = 'Veuillez sélectionner une zone du corps';
        }
        if (!formData.piercingTypeId) {
          newErrors.piercingTypeId = 'Veuillez sélectionner un type de piercing';
        }
        if (!formData.availableDate) {
          newErrors.availableDate = 'Veuillez sélectionner une date';
        }
        break;

      case 5:
        if (!formData.shopId) {
          newErrors.shopId = 'Veuillez sélectionner un shop';
        }
        if (!formData.selectedTimeSlot) {
          newErrors.selectedTimeSlot = 'Veuillez sélectionner un créneau horaire';
        }

        if (
          Object.keys(newErrors).length === 0
          && formData.clientId
          && formData.shopId
          && formData.availableDate
          && formData.selectedTimeSlot
        ) {
          const piercingsPayload = piercingCart.length > 0
            ? piercingCart.map((piercing) => ({
                bodyZoneId: piercing.bodyZoneId,
                piercingTypeId: piercing.piercingTypeId,
                selectedSupplements: piercing.selectedSupplements || [],
              }))
            : [{
                bodyZoneId: formData.bodyZoneId,
                piercingTypeId: formData.piercingTypeId,
                selectedSupplements: formData.selectedSupplements || [],
              }];
          const selectedDate = formData.availableDate;
          const selectedTime = formData.selectedTimeSlot;
          const conflictCheck = await bookingApi.checkPiercingConflict({
            clientId: formData.clientId,
            shopId: formData.shopId,
            availableDate: selectedDate,
            selectedTimeSlot: selectedTime,
            piercings: piercingsPayload,
          });

          if (conflictCheck.success && conflictCheck.data?.conflict) {
            setError(null);
            setBookingConflictSelection({ date: selectedDate, time: selectedTime });
            setBookingConflictMessage(
              conflictCheck.data.conflictReason === 'shop_unavailable'
                ? 'Ce créneau est déjà réservé dans ce shop.'
                : 'Ce client a déjà un rendez-vous sur ce créneau horaire.',
            );
            return false;
          }
        }
        break;

      case 6:
        // Customer details validation - clientId is required
        if (!formData.clientId) {
          newErrors.clientId = 'Les informations client sont requises';
        }
        break;

      case 7:
        if (!formData.paymentMethod) {
          newErrors.paymentMethod = 'Veuillez sélectionner une méthode de paiement';
        }

        if (formData.totalPrice <= 0) {
          newErrors.totalPrice = 'Le prix total doit être supérieur à 0';
        }

        if (!refundPolicyAccepted) {
          newErrors.refundPolicy =
            'Vous devez accepter la politique de remboursement';
        }

        if (!termsAccepted) {
          newErrors.terms =
            'Vous devez accepter les conditions générales';
        }

        break;
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getSummaryPiercings = (): BookingPiercingItem[] => (
    piercingCart.length > 0
      ? piercingCart
      : [{
          id: 'current',
          bodyZoneId: formData.bodyZoneId,
          piercingTypeId: formData.piercingTypeId,
          selectedSupplements: formData.selectedSupplements,
          noteToPractitioner: htmlToText(formData.noteToPractitioner || '').trim()
            ? formData.noteToPractitioner
            : undefined,
          piercingTypeName: piercingTypes.find(p => p.id === formData.piercingTypeId)?.name,
          bodyZoneName: bodyZones.find(zone => zone.id === formData.bodyZoneId)?.name,
          basePrice: Number(piercingTypes.find(p => p.id === formData.piercingTypeId)?.basePrice) || 0,
        }]
  );

  const getPiercingSummaryDetails = (piercing: BookingPiercingItem) => {
    const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
    const bodyZone = bodyZones.find(zone => zone.id === piercing.bodyZoneId);

    return {
      name: piercingType?.name || piercing.piercingTypeName || 'Piercing',
      bodyZoneName: bodyZone?.name || piercing.bodyZoneName || '',
      basePrice: Number(piercingType?.basePrice ?? piercing.basePrice ?? 0) || 0,
    };
  };

  const handleCustomerComplete = (clientId: string, clientData: ClientData) => {
    setFormData((prev) => ({
      ...prev,
      clientId,
      preferredLanguage: clientData.preferredLanguage,
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      email: clientData.email,
      phone: clientData.phone,
      dateOfBirth: clientData.dateOfBirth,
      isMinor: clientData.isMinor ?? null,
      isAnonymous: clientData.isAnonymous,
      anonymousCode: clientData.anonymousCode,
      emergencyContact: clientData.emergencyContact || prev.emergencyContact,
    }));
    if (!existingClient && !isAuthenticated) {
      setClientCreatedNotice(
        clientData.isAnonymous ? 'anonymous' : clientData.accountCreated ? 'account' : 'guest',
      );
    }
    setExistingClient({
      id: clientId,
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      email: clientData.email,
      phone: clientData.phone,
      dateOfBirth: clientData.dateOfBirth,
      isAnonymous: clientData.isAnonymous,
      anonymousCode: clientData.anonymousCode,
      emergencyContact: clientData.emergencyContact,
    });
    setCurrentStep(5);
    scrollBookingIntoView(bookingTopRef.current);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // When user is already authenticated, show confirmation instead of email/phone form
        if (isAuthenticated && user) {
          const customerInitialDataAuth = existingClient ? {
            id: existingClient.id,
            firstName: existingClient.firstName,
            lastName: existingClient.lastName,
            email: existingClient.email,
            phone: existingClient.phone,
            dateOfBirth: existingClient.dateOfBirth || '',
            preferredLanguage: 'fr',
            emergencyContact: formData.emergencyContact
          } : {
            id: (user as any).id,
            firstName: (user as any).firstName || '',
            lastName: (user as any).lastName || '',
            email: (user as any).email || '',
            phone: (user as any).phone || '',
            dateOfBirth: user.dateOfBirth || '',
            preferredLanguage: (user as any).preferredLanguage || formData.preferredLanguage,
            emergencyContact: formData.emergencyContact
          };
          return (
            <div className="space-y-8">
              <CustomerDetailsStep
                onComplete={(clientId, clientData) => {
                  setFormData(prev => ({
                    ...prev,
                    clientId,
                    preferredLanguage: clientData.preferredLanguage,
                    firstName: clientData.firstName,
                    lastName: clientData.lastName,
                    email: clientData.email,
                    phone: clientData.phone,
                    dateOfBirth: clientData.dateOfBirth,
                    isMinor: clientData.isMinor ?? null,
                    isAnonymous: clientData.isAnonymous,
                    anonymousCode: clientData.anonymousCode,
                    emergencyContact: clientData.emergencyContact || prev.emergencyContact
                  }));
                  setExistingClient({
                    id: clientId,
                    firstName: clientData.firstName,
                    lastName: clientData.lastName,
                    email: clientData.email,
                    phone: clientData.phone,
                    dateOfBirth: clientData.dateOfBirth,
                    isAnonymous: clientData.isAnonymous,
                    anonymousCode: clientData.anonymousCode,
                    emergencyContact: clientData.emergencyContact,
                  });
                  setCurrentStep(5);
                  scrollBookingIntoView(bookingTopRef.current);
                }}
                onBack={handlePrevious}
                onLogout={() => {
                  setExistingClient(null);
                  setFormData(prev => ({ ...prev, clientId: undefined }));
                }}
                bookingType="piercing"
                allowAnonymousBooking={piercingBookingAnonymousEnabled}
                requireLegalGuardian={true}
                preferredLanguage={formData.preferredLanguage}
                initialData={customerInitialDataAuth}
                contactInfo={{
                  email: contactInfo.email || formData.email,
                  phone: contactInfo.phone || formData.phone
                }}
                lookupStatus={contactLookupStatus}
              />
            </div>
          );
        }

        return (
          <div className="space-y-8">
            {formData.isAnonymous && formData.clientId ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-textPrimary mb-2">
                  {t('piercingBooking.clientCreated.anonymousLabel')}
                </h2>
                {formatPersonName(formData.firstName, formData.lastName) ? (
                  <p className="text-lg font-medium text-textPrimary">
                    {formatPersonName(formData.firstName, formData.lastName)}
                  </p>
                ) : null}
                {formData.anonymousCode ? (
                  <p className="mt-2 font-mono text-lg tracking-wider text-textPrimary">
                    {formData.anonymousCode}
                  </p>
                ) : null}
              </div>
            ) : formData.isAnonymous ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <GuestBookingForm
                  anonymous
                  hideNavigation
                  formId="piercing-anonymous-guest-form"
                  onBusyChange={setAnonymousFormBusy}
                  requireLegalGuardian={false}
                  preferredLanguage={formData.preferredLanguage || 'fr'}
                  initialValues={{
                    firstName: formData.firstName,
                    preferredLanguage: formData.preferredLanguage || 'fr',
                  }}
                  onComplete={(clientData) => {
                    handleCustomerComplete(clientData.id, {
                      ...clientData,
                      isAnonymous: true,
                    });
                  }}
                />
              </div>
            ) : (
            <>
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-textPrimary mb-4">{t('piercingBooking.contactInitial.title')}</h2>
              <p className="text-sm text-gray-600 mb-6">
                {piercingBookingAnonymousEnabled
                  ? t('piercingBooking.contactInitial.description')
                  : t('piercingBooking.contactInitial.descriptionWithoutAnonymous')}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                      className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="votre@email.com"
                    />
                  </div>
                  {fieldErrors.email && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Téléphone
                  </label>
                  <InternationalPhoneInput
                      value={contactPhoneDisplay}
                      country={contactPhoneCountry}
                      onChange={(result) => {
                        setContactPhoneDisplay(result.displayValue);
                        setContactPhoneCountry(result.country);
                        setContactPhoneE164(result.e164Value);
                        setContactPhoneIsValid(result.isValid);

                        handleContactChange(
                          'phone',
                          result.displayValue,
                        );

                        if (result.isValid && fieldErrors.phone) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.phone;
                            return next;
                          });
                        }
                      }}
                      selectClassName="w-full px-4 py-3 text-base border rounded-lg border-gray-300"
                      inputClassName={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        fieldErrors.phone
                          ? 'border-red-300'
                          : 'border-gray-300'
                      }`}
                      error={fieldErrors.phone}
                    />
                </div>
              </div>

              {fieldErrors.contact && (
                <p className="text-red-600 text-sm mt-3" role="alert">
                  {fieldErrors.contact}
                </p>
              )}

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <Shield className="w-4 h-4 inline mr-1" />
                  {t('piercingBooking.contactInitial.requirement')}
                </p>
              </div>

              {piercingBookingAnonymousEnabled && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-3 text-gray-500">
                        {t('piercingBooking.contactInitial.or')}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowClientConfirmation(false);
                      setFieldErrors({});
                      setFormData((prev) => ({
                        ...prev,
                        isAnonymous: true,
                        clientId: undefined,
                        anonymousCode: undefined,
                      }));
                      scrollBookingIntoView(bookingTopRef.current);
                    }}
                    className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3 mb-1">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="font-semibold text-lg">
                        {t('piercingBooking.contactInitial.continueAnonymous')}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      {t('piercingBooking.contactInitial.anonymousHint')}
                    </p>
                  </button>
                </>
              )}
            </div>

            {/* Existing Client Confirmation */}
            {showClientConfirmation && existingClientFound && (
              <div className="fo-panel rounded-lg p-6">
                <ModalHeader
                  title={
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg shrink-0">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-textPrimary">
                        {t('piercingBooking.existingClient.title')}
                      </h3>
                    </div>
                  }
                  onClose={handleDismissClientConfirmation}
                  disabled={loading}
                  className="mb-4"
                />
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    {t('piercingBooking.existingClient.description')}
                  </p>
                  <div className="space-y-1">
                    <p className="font-medium">{formatPersonName(existingClientFound.firstName, existingClientFound.lastName)}</p>
                    {existingClientFound.email && <p className="text-sm">📧 {existingClientFound.email}</p>}
                    {existingClientFound.phone && <p className="text-sm">📱 {existingClientFound.phone}</p>}
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-700">
                    {t('piercingBooking.existingClient.verificationInfo', {
                      method: contactInfo.email.trim() 
                        ? t('piercingBooking.existingClient.verificationInfoEmail')
                        : t('piercingBooking.existingClient.verificationInfoPhone')
                    })}
                  </p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleUseExistingClient}
                    disabled={loading}
                    className="btn-primary flex-1 disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {t('piercingBooking.otp.sending')}
                      </div>
                    ) : (
                      t('piercingBooking.existingClient.useAccount')
                    )}
                  </button>
                  <button
                    onClick={handleCreateNewClient}
                    className="btn-secondary flex-1"
                    disabled={loading}
                  >
                    {t('piercingBooking.existingClient.createNew')}
                  </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleDismissClientConfirmation}
                    disabled={loading}
                    className="btn-secondary sm:self-end"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        );

      case 2:
        // OTP Verification Step
        if (!otpSent && !otpVerified) {
          return null; // Skip this step if no OTP was sent
        }

        // OTP verified - show confirmation before continuing
        if (otpVerified && existingClientFound) {
          const displayName = formatPersonName(formData.firstName, formData.lastName)
            || formData.email
            || existingClientFound.email;
          return (
            <div className="space-y-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-green-800 mb-2">
                    {t('piercingBooking.otp.verifiedTitle')}
                  </h2>
                  <p className="text-green-700 mb-6">
                    {t('piercingBooking.otp.verifiedMessage', { name: displayName })}
                  </p>
                  <button
                    onClick={() => {
                      setOtpVerified(false);
                      setOtpSent(false);
                      // Client data already populated by handleOtpVerification;
                      // set clientId in formData and skip step 6 (CustomerDetailsStep)
                      // to avoid a duplicate confirmation screen.
                      setFormData(prev => ({ ...prev, clientId: existingClientFound.id }));
                      setCurrentStep(5);
                      scrollBookingIntoView(bookingTopRef.current);
                    }}
                    className="btn-primary flex items-center justify-center gap-2 mx-auto"
                  >
                    <span>{t('common.next')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        }
        
        return (
          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                
                <h2 className="text-xl font-semibold text-textPrimary mb-2">
                  {t('piercingBooking.otp.title')}
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  {otpChannel === 'sms'
                    ? `Un code de vérification a été envoyé par SMS${otpDestinationHint ? ` (${otpDestinationHint})` : ''}`
                    : t('piercingBooking.otp.sentTo', { contact: otpDestinationHint || existingClientFound?.email || '' })}
                </p>
                
                <div className="max-w-xs mx-auto">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('piercingBooking.otp.codeLabel')}
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className={`w-full px-4 py-3 text-base text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      fieldErrors.otpCode ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="000000"
                  />
                  {fieldErrors.otpCode && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.otpCode}</p>
                  )}
                  {typeof otpAttemptsLeft === 'number' && otpAttemptsLeft >= 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {t('piercingBooking.otp.attemptsLeft', { count: otpAttemptsLeft })}
                    </p>
                  )}
                </div>
                
                <button
                  onClick={handleOtpVerification}
                  disabled={loading || otpCode.length !== 6}
                  className="btn-primary mt-4 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Vérification...
                    </div>
                  ) : (
                    'Vérifier le code'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading || resendLimitReached}
                  className="btn-secondary mt-3 disabled:opacity-50"
                >
                  {t('piercingBooking.otp.resend')}
                </button>
                {resendLimitReached && (
                  <p className="text-sm text-amber-700 mt-2">
                    {t('piercingBooking.otp.resendLimitReached')}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3: {
        const selectedBodyZone = bodyZones.find((z) => z.id === formData.bodyZoneId) ?? null;
        const zoneGuide =
          piercingBookingZoneGuideEnabled
            ? getPiercingZoneGuide(selectedBodyZone)
            : null;
        const selectedPiercingTypeName =
          piercingTypes.find((p) => p.id === formData.piercingTypeId)?.name || '';
        const previewPiercingTypeName =
          piercingTypes.find((p) => p.id === previewPiercingTypeId)?.name || '';

        return (
          <div
            className={
              isAddingAnotherPiercing
                ? 'space-y-8'
                : 'grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]'
            }
          >
            {!isAddingAnotherPiercing && (
              <div
                className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50 shadow-sm"
                role="note"
              >
                <div className="flex flex-col gap-3 p-4 sm:p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-600 text-white">
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1 space-y-3">
                    <div>
                      <p className="text-base font-bold leading-snug text-red-600 sm:text-lg">
                        1 RENDEZ-VOUS = 1 PERSONNE !
                      </p>
                      <p className="mt-1 text-sm sm:text-base">
                        Le rendez-vous ne peut concerner qu'une seule personne.
                      </p>
                    </div>
                    <div className="pt-3">
                      <p className="text-sm font-bold text-red-600 sm:text-base">
                        Vous réservez pour un mineur ?
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-blue-900 sm:text-base">
                        Le rendez-vous doit être pris en son nom, et non au nom de son représentant légal.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm">
              {isAddingAnotherPiercing && (
                <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <span>Ajout d'un piercing supplémentaire</span>
                  <button
                    type="button"
                    onClick={handleCancelAdditionalPiercing}
                    className="btn-secondary"
                  >
                    Annuler l'ajout
                  </button>
                </div>
              )}
              <h2 className="text-xl font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step1.title')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('piercingBooking.step1.description')}
              </p>
              <div
                className={`grid grid-cols-1 gap-6 ${
                  zoneGuide ? 'lg:grid-cols-2 lg:gap-8 lg:items-start' : ''
                }`}
              >
                <div className="min-w-0 space-y-6">
                  {/* Body Zone Selection */}
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('piercingBooking.fields.bodyZone')} *
                    </label>
                    <ThemeSelect
                      value={formData.bodyZoneId}
                      placeholder={t('piercingBooking.placeholders.chooseBodyZone')}
                      aria-invalid={Boolean(fieldErrors.bodyZoneId)}
                      aria-label={t('piercingBooking.fields.bodyZone')}
                      className={fieldErrors.bodyZoneId ? 'border-red-400' : ''}
                      onChange={(nextZoneId) => {
                        handleInputChange('bodyZoneId', nextZoneId);
                        handleInputChange('piercingTypeId', '');
                        setPreviewPiercingTypeId('');
                      }}
                      options={[
                        {
                          value: '',
                          label: t('piercingBooking.placeholders.chooseBodyZone'),
                        },
                        ...bodyZones.map((zone) => {
                          const alreadySelected = piercingCart.some(
                            (p) => p.bodyZoneId === zone.id && p.id !== editingPiercingId,
                          );
                          const minorOnly = minorOnlyZoneIds.has(zone.id);
                          return {
                            value: zone.id,
                            label: zone.name,
                          };
                        }),
                      ]}
                    />
                    {fieldErrors.bodyZoneId && (
                      <p className="text-red-600 text-sm mt-1">{fieldErrors.bodyZoneId}</p>
                    )}
                    <p className="text-sm text-red-600 pt-2">{t('piercingBooking.step1.minorNotice')}</p>
      
                  </div>

                  {/* Mobile / tablet: guide directly under body zone for context while choosing type */}
                  {zoneGuide && (
                    <div className="lg:hidden">
                      <PiercingZoneGuidePanel
                        guide={zoneGuide}
                        selectedPiercingTypeId={formData.piercingTypeId}
                        selectedPiercingTypeName={selectedPiercingTypeName}
                        previewPiercingTypeId={previewPiercingTypeId}
                        previewPiercingTypeName={previewPiercingTypeName}
                      />
                    </div>
                  )}

                  {/* Piercing Type Selection */}
                  {formData.bodyZoneId && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('piercingBooking.fields.piercingName')} *
                      </label>
                      <ThemeSelect
                        value={formData.piercingTypeId}
                        placeholder={t('piercingBooking.placeholders.choosePiercing')}
                        aria-invalid={Boolean(fieldErrors.piercingTypeId)}
                        aria-label={t('piercingBooking.fields.piercingName')}
                        className={fieldErrors.piercingTypeId ? 'border-red-400' : ''}
                        onChange={(nextTypeId) => {
                          handleInputChange('piercingTypeId', nextTypeId);
                          setPreviewPiercingTypeId('');
                        }}
                        onOptionHighlight={setPreviewPiercingTypeId}
                        options={[
                          {
                            value: '',
                            label: t('piercingBooking.placeholders.choosePiercing'),
                          },
                          ...piercingTypes
                            .filter((piercing) => piercing.bodyZoneId === formData.bodyZoneId)
                            .map((piercing) => ({
                              value: piercing.id,
                              label: `${piercing.name} - ${piercing.basePrice}€`
                            })),
                        ]}
                      />
                      {fieldErrors.piercingTypeId && (
                        <p className="text-red-600 text-sm mt-1">{fieldErrors.piercingTypeId}</p>
                      )}
                    </div>
                  )}

                  {/* Note for practitioner */}
                  {formData.piercingTypeId && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('piercingBooking.fields.noteToPractitioner')}
                      </label>
                      <RichTextEditor
                        value={formData.noteToPractitioner}
                        onChange={(value) => handleInputChange('noteToPractitioner', value)}
                        placeholder={t('piercingBooking.placeholders.noteToPractitioner')}
                      />
                    </div>
                  )}

                  {/* Date Selection */}
                  {formData.piercingTypeId && (
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('piercingBooking.fields.availableDate')} *
                      </label>
                      <DateInput
                        value={formData.availableDate}
                        onChange={(e) => handleInputChange('availableDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`input-field ${
                          fieldErrors.availableDate ? 'border-red-400' : ''
                        }`}
                      />
                      {fieldErrors.availableDate && (
                        <p className="text-red-600 text-sm mt-1">{fieldErrors.availableDate}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop: sticky guide beside the form */}
                {zoneGuide && (
                  <aside className="hidden lg:block lg:sticky lg:top-6">
                    <PiercingZoneGuidePanel
                      guide={zoneGuide}
                      selectedPiercingTypeId={formData.piercingTypeId}
                      selectedPiercingTypeName={selectedPiercingTypeName}
                      previewPiercingTypeId={previewPiercingTypeId}
                      previewPiercingTypeName={previewPiercingTypeName}
                    />
                  </aside>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 4:
        if (!supplementsStepEnabled) {
          return null;
        }
        return (
          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step2.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {t('piercingBooking.step2.description')}
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  {t('piercingBooking.step2.note')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {supplements.map((supplement) => (
                  <div key={supplement.id} className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.selectedSupplements.includes(supplement.id)}
                        onChange={(e) => {
                          const newSupplements = e.target.checked
                            ? [...formData.selectedSupplements, supplement.id]
                            : formData.selectedSupplements.filter(id => id !== supplement.id);
                          handleInputChange('selectedSupplements', newSupplements);
                        }}
                        className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-base font-medium text-textPrimary">{supplement.name}</h3>
                          <span className="text-base font-bold text-accent-600">+{supplement.price}€</span>
                        </div>
                        <p className="text-sm text-textSecondary">{supplement.description}</p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8">
            {clientCreatedNotice && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                <p className="font-semibold">{t('piercingBooking.clientCreated.summaryTitle')}</p>
                <p>
                  {clientCreatedNotice === 'account'
                    ? t('piercingBooking.clientCreated.summaryAccount')
                    : clientCreatedNotice === 'anonymous'
                      ? t('piercingBooking.clientCreated.summaryAnonymous')
                      : t('piercingBooking.clientCreated.summaryGuest')}
                </p>
                <p className="mt-1">
                  {clientIdentityLine
                    ? t('piercingBooking.clientCreated.summaryReservation', { identity: clientIdentityLine })
                    : t('piercingBooking.clientCreated.summaryFallback')}
                </p>
                <p className="mt-2 text-xs text-green-700">
                  {clientCreatedNotice === 'account'
                    ? t('piercingBooking.clientCreated.account')
                    : clientCreatedNotice === 'anonymous'
                      ? t('piercingBooking.clientCreated.anonymous')
                      : t('piercingBooking.clientCreated.guest')}
                </p>
              </div>
            )}
            {piercingCart.length > 1 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                <p className="font-semibold">{t('piercingBooking.multiDate.title')}</p>
                <p>{t('piercingBooking.multiDate.description')}</p>
              </div>
            )}
            {/* Cart Preview */}
            {piercingCart.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-3">
                  {t('piercingBooking.cart.title')} ({piercingCart.length})
                </h3>
                {customerSummary && (customerSummary.name || customerSummary.email || customerSummary.phone || customerSummary.isAnonymous) && (
                  <div className="mb-3 pb-1">
                    <div className="flex items-start gap-2">
                      <User className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-0.5">
                          {t('piercingBooking.cart.customer')}
                        </p>
                        {customerSummary.name && (
                          <p className="text-sm font-medium text-blue-900">{customerSummary.name}</p>
                        )}
                        {customerSummary.isAnonymous && (
                          <p className="text-xs text-blue-700 mt-0.5">
                            {t('piercingBooking.clientCreated.anonymousLabel')}
                          </p>
                        )}
                        {customerSummary.anonymousCode && (
                          <p className="text-xs font-mono text-blue-900 mt-0.5">
                            {customerSummary.anonymousCode}
                          </p>
                        )}
                        {(customerSummary.email || customerSummary.phone) && (
                          <p className="text-xs text-blue-700 mt-0.5">
                            {[customerSummary.email, customerSummary.phone].filter(Boolean).join(' • ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {piercingCart.map((piercing) => {
                    const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
                    const bodyZone = bodyZones.find(bz => bz.id === piercing.bodyZoneId);
                    const noteText = htmlToText(piercing.noteToPractitioner || '').trim();
                    return (
                      <div key={piercing.id} className="text-sm text-blue-800">
                        <div className="font-medium">
                          {piercingType?.name} ({bodyZone?.name})
                        </div>
                        {noteText && (
                          <div
                            className="ml-4 mt-1 text-xs text-blue-700"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeRichTextHtml(piercing.noteToPractitioner || '')
                            }}
                          />
                        )}
                        {(piercing.selectedSupplements || []).length > 0 && (
                          <div className="ml-4 mt-1 space-y-1">
                            {(piercing.selectedSupplements || []).map((suppId: string) => {
                              const supplement = supplements.find(s => s.id === suppId);
                              return supplement ? (
                                <div key={suppId} className="text-xs text-blue-700">
                                  + {supplement.name} (+{supplement.price}€)
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-sm font-medium text-blue-900">
                  {(() => {
                    // Calculate total directly from cart to ensure accuracy
                    const calculatedTotal = piercingCart.reduce((total, piercing) => {
                      const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
                      if (!piercingType) return total;
                      
                      const basePrice = piercingType.basePrice;
                      const supplementsArray = Array.isArray(piercing.selectedSupplements) 
                        ? piercing.selectedSupplements 
                        : [];
                      const supplementsTotal = supplementsArray.reduce((suppTotal: number, suppId: string) => {
                        const supplement = supplements.find(s => s.id === suppId);
                        return suppTotal + (supplement?.price || 0);
                      }, 0);
                      
                      return total + basePrice + supplementsTotal;
                    }, 0);
                    
                    return t('piercingBooking.cart.total', { amount: calculatedTotal });
                  })()}
                </div>
              </div>
            )}
            
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step3.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {t('piercingBooking.step3.description')}
              </p>

              {/* Shop Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-textPrimary mb-4">
                  {t('piercingBooking.fields.shop')} *
                </label>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-textSecondary">Chargement des boutiques...</span>
                  </div>
                ) : shops.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-yellow-800 mb-2">
                          Aucune boutique disponible
                        </h4>
                        <p className="text-sm text-yellow-700 mb-3">
                          Aucune boutique n'est actuellement disponible pour les piercings sélectionnés.
                        </p>
                        <div className="text-sm text-yellow-700 space-y-1">
                          <p className="font-medium mb-2">Causes possibles :</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Les boutiques ne proposent pas ce type de piercing</li>
                            <li>Aucun praticien n'est disponible pour ce service</li>
                            <li>Les boutiques sont temporairement fermées</li>
                          </ul>
                        </div>
                        <div className="mt-4 pt-4 border-t border-yellow-300">
                          <p className="text-sm text-yellow-700 font-medium mb-2">Solutions :</p>
                          <ul className="text-sm text-yellow-700 space-y-1">
                            <li>• Vérifiez que vous avez bien sélectionné un piercing</li>
                            <li>• Essayez de sélectionner un autre type de piercing</li>
                            <li>• Contactez-nous directement pour réserver : <a href="tel:+33123456789" className="underline font-medium">+33 1 23 45 67 89</a></li>
                            <li>• Ou envoyez-nous un email : <a href="mailto:contact@digitaltattoo.com" className="underline font-medium">contact@digitaltattoo.com</a></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {shops.map((shop) => (
                      <label key={shop.id} className="cursor-pointer">
                        <input
                          type="radio"
                          name="shop"
                          value={shop.id}
                          checked={formData.shopId === shop.id}
                          onChange={(e) => handleInputChange('shopId', e.target.value)}
                          className="sr-only"
                        />
                        <div className={`border-2 rounded-lg p-4 transition-colors ${
                          formData.shopId === shop.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <h3 className="text-base font-medium text-textPrimary">{shop.name}</h3>
                          <p className="text-sm text-textSecondary mt-1">
                            {shop.address.street}, {shop.address.city}
                          </p>
                          <p className="text-sm text-textSecondary">{shop.phone}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                {fieldErrors.shopId && (
                  <p className="text-red-600 text-sm mt-2">{fieldErrors.shopId}</p>
                )}
                {hasVisibleHtmlContent(piercingPlanningRecommendation) && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                    <div
                      className="[&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: piercingPlanningRecommendation }}
                    />
                  </div>
                )}
              </div>

              {/* Date Selection - Allow changing date in this step */}
              {formData.shopId && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('piercingBooking.fields.availableDate')} *
                  </label>
                  <DateInput
                    value={formData.availableDate}
                    onChange={(e) => {
                      handleInputChange('availableDate', e.target.value);
                      handleInputChange('selectedTimeSlot', ''); // Clear selected time slot when date changes
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      fieldErrors.availableDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {fieldErrors.availableDate && (
                    <p className="text-red-600 text-sm mt-1">{fieldErrors.availableDate}</p>
                  )}
                  {piercingConflict?.conflictScope === 'date' && piercingConflict.conflictAppointment && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                      <p className="font-semibold mb-1">Réservation impossible</p>
                      <p>
                        Ce client a déjà un rendez-vous piercing prévu le{' '}
                        {new Date(piercingConflict.conflictAppointment.scheduledAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}.
                      </p>
                      {piercingConflict.conflictAppointment.piercings?.length > 0 && (
                        <p className="mt-1">
                          Piercing(s) : {piercingConflict.conflictAppointment.piercings.join(', ')}
                        </p>
                      )}
                      <p className="mt-1">
                        Veuillez choisir une autre date éloignée d'au moins {piercingConflict.minGapHours} heure{piercingConflict.minGapHours > 1 ? 's' : ''}.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Time Slot Selection - Enhanced Planning */}
              {formData.shopId && formData.availableDate && (
                <div>
                  {piercingConflict?.conflictScope !== 'date' && (
                    <>
                      <label className="block text-sm font-medium text-textPrimary mb-4">
                        {t('piercingBooking.step3.availableSlots')} {new Date(formData.availableDate).toLocaleDateString('fr-FR')}
                      </label>
                      {checkingPiercingConflict && (
                        <div className="mb-4 flex items-center text-sm text-gray-600">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Vérification des réservations en cours...
                        </div>
                      )}
                      {piercingConflict?.conflictScope === 'time' && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                          {piercingConflict.conflictReason === 'shop_unavailable' ? (
                            <>
                              <p className="font-semibold mb-1">Créneau indisponible</p>
                              <p>Ce créneau vient d&apos;être réservé. Veuillez en choisir un autre.</p>
                            </>
                          ) : piercingConflict.conflictAppointment ? (
                            <>
                              <p className="font-semibold mb-1">Réservation impossible</p>
                              <p>
                                Ce client a déjà un rendez-vous piercing prévu le{' '}
                                {new Date(piercingConflict.conflictAppointment.scheduledAt).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}.
                              </p>
                              {piercingConflict.conflictAppointment.piercings?.length > 0 && (
                                <p className="mt-1">
                                  Piercing(s) : {piercingConflict.conflictAppointment.piercings.join(', ')}
                                </p>
                              )}
                              <p className="mt-1">
                                Veuillez choisir une autre date éloignée d&apos;au moins {piercingConflict.minGapHours} heure{piercingConflict.minGapHours > 1 ? 's' : ''}.
                              </p>
                            </>
                          ) : null}
                        </div>
                      )}
                    </>
                  )}
                  {piercingConflict?.conflictScope === 'date' ? null : (
                    loadingSlots ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                        <span className="ml-2 text-textSecondary">Chargement des créneaux disponibles...</span>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                          <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                          <p className="text-yellow-800 font-medium mb-2">Aucun créneau disponible pour cette date.</p>
                          <p className="text-sm text-yellow-700">Veuillez sélectionner une autre date ci-dessus.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {availableSlots.map((slot, index) => {
                          const slotTime = extractSlotLocalTime(slot.start);
                          const isSelected = formData.selectedTimeSlot === slotTime;
                          // Use ISO string as key to ensure uniqueness (already deduplicated)
                          return (
                            <button
                              key={`${slot.start}-${index}`}
                              type="button"
                              onClick={() => {
                                handleInputChange('selectedTimeSlot', slotTime);
                              }}
                              disabled={!slot.isAvailable}
                              className={`px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-500 text-white'
                                  : slot.isAvailable
                                    ? 'border-gray-200 hover:border-primary-300 text-textPrimary'
                                    : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              {slotTime}
                            </button>
                          );
                        })}
                      </div>
                    )
                  )}
                  {fieldErrors.selectedTimeSlot && (
                    <p className="text-red-600 text-sm mt-2">{fieldErrors.selectedTimeSlot}</p>
                  )}
                </div>
              )}
            </div>

          </div>
        );

      case 6:
        // Piercing booking: legal guardian not required in this flow
        const requireLegalGuardian = true;
        const customerInitialData = existingClient ? {
          id: existingClient.id,
          firstName: existingClient.firstName,
          lastName: existingClient.lastName,
          email: existingClient.email,
          phone: existingClient.phone,
          dateOfBirth: existingClient.dateOfBirth || '',
          preferredLanguage: 'fr',
          emergencyContact: formData.emergencyContact
        } : isAuthenticated && user ? {
          id: (user as any).id,
          firstName: (user as any).firstName || '',
          lastName: (user as any).lastName || '',
          email: (user as any).email || '',
          phone: (user as any).phone || '',
          dateOfBirth: user.dateOfBirth || '',
          preferredLanguage: (user as any).preferredLanguage || formData.preferredLanguage,
          emergencyContact: formData.emergencyContact
        } : {
          email: contactInfo.email || formData.email,
          phone: contactInfo.phone || formData.phone,
          preferredLanguage: formData.preferredLanguage,
          emergencyContact: formData.emergencyContact
        };
        
        return (
          <div className="space-y-8">
            <CustomerDetailsStep
              onComplete={handleCustomerComplete}
              onBack={handlePrevious}
              onLogout={() => {
                setExistingClient(null);
                setFormData(prev => ({ ...prev, clientId: undefined }));
              }}
              bookingType="piercing"
              allowAnonymousBooking={piercingBookingAnonymousEnabled}
              requireLegalGuardian={requireLegalGuardian}
              preferredLanguage={formData.preferredLanguage}
              initialData={customerInitialData}
              lockToAnonymous={Boolean(formData.isAnonymous)}
            contactInfo={{
              email: contactInfo.email || formData.email,
              phone: contactInfo.phone || formData.phone
            }}
            lookupStatus={contactLookupStatus}
            />
          </div>
        );

      case 7:
        return (
          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step5.title')}
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {t('piercingBooking.step5.description')}
              </p>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-textPrimary mb-4">
                  {t('piercingBooking.step5.summary')}
                </h3>
                
                <div className="divide-y divide-gray-200">
                  {/* Show all piercings in cart */}
                  {getSummaryPiercings().map((piercing, index) => {
                    const details = getPiercingSummaryDetails(piercing);

                    return (
                      <div key={piercing.id} className="py-3 first:pt-0">
                        <div className="flex justify-between gap-4 mb-2">
                          <span className="text-textSecondary font-medium">
                            {piercingCart.length > 1 ? `Piercing #${index + 1}: ` : ''}{details.name}
                            {details.bodyZoneName && ` (${details.bodyZoneName})`}
                          </span>
                          <span className="font-medium whitespace-nowrap">{details.basePrice}€</span>
                        </div>
                        
                        {(piercing.selectedSupplements || []).map((suppId: string) => {
                          const supplement = supplements.find(s => s.id === suppId);
                          return supplement ? (
                            <div key={suppId} className="flex justify-between ml-4 text-sm">
                              <span className="text-textSecondary">+ {supplement.name}</span>
                              <span className="font-medium">+{supplement.price}€</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-bold">
                  <span>{t('piercingBooking.step5.total')}</span>
                  <span className="text-accent-600">{formData.totalPrice}€</span>
                </div>

                {/* Legal consents */}
<div className="border-t border-gray-200 mt-5 pt-5 space-y-4">

  {/* Refund policy */}
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={refundPolicyAccepted}
      onChange={(e) => {
        setRefundPolicyAccepted(e.target.checked);

        if (e.target.checked) {
          setFieldErrors((prev) => {
            const next = { ...prev };
            delete next.refundPolicy;
            return next;
          });
        }
      }}
      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
    />

    <span className="text-sm text-gray-700">
      <Trans
        i18nKey="piercingBooking.step5.refundPolicyConsent"
        components={{
          refundPolicyLink: (
            <button
              type="button"
              className="font-medium text-primary-600 underline hover:text-primary-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenLegalModal('refundPolicy');
              }}
            />
          ),
        }}
      />
    </span>
  </label>

  {fieldErrors.refundPolicy && (
    <p className="text-sm text-red-600 ml-7">
      {fieldErrors.refundPolicy}
    </p>
  )}

  {/* Terms & Conditions */}
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={termsAccepted}
      onChange={(e) => {
        setTermsAccepted(e.target.checked);

        if (e.target.checked) {
          setFieldErrors((prev) => {
            const next = { ...prev };
            delete next.terms;
            return next;
          });
        }
      }}
      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
    />

    <span className="text-sm text-gray-700">
      <Trans
        i18nKey="piercingBooking.step5.termsConsent"
        components={{
          termsLink: (
            <button
              type="button"
              className="font-medium text-primary-600 underline hover:text-primary-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpenLegalModal('terms');
              }}
            />
          ),
        }}
      />
    </span>
  </label>

  {fieldErrors.terms && (
    <p className="text-sm text-red-600 ml-7">
      {fieldErrors.terms}
    </p>
  )}

</div>
              </div>

              {/* Payment Method */}
              <div>
                <h3 className="text-lg font-semibold text-textPrimary mb-4">
                  {t('piercingBooking.step5.paymentMethod')}
                </h3>
                
                <div className="space-y-3">
                  {ENABLED_PIERCING_PAYMENT_METHOD_OPTIONS.map((method) => {
                    const MethodIcon = paymentMethodIcons[method.value];

                    return (
                    <label key={method.value} className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.value}
                        checked={formData.paymentMethod === method.value}
                        onChange={(e) => handleInputChange('paymentMethod', e.target.value as PiercingPaymentMethod)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <MethodIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-base text-textPrimary">{t(method.labelKey)}</span>
                    </label>
                    );
                  })}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <p className="text-yellow-800 text-sm">
                    {t('piercingBooking.step5.depositInfo', { amount: formData.depositAmount })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 8:
        // Deposit payment step
        return (
          <div className="space-y-8">
            {isTestMode && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
                <span className="text-lg leading-none">⚠️</span>
                <span>
                  <strong>Mode test activé</strong> — ce paiement sera traité via la passerelle active (Stripe ou Viva.com) en mode test.
                  Aucun montant réel ne sera débité.
                </span>
              </div>
            )}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="text-center">
                <div className="flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mx-auto mb-6">
                  <CreditCard className="w-12 h-12 text-amber-600" />
                </div>

                <h1 className="text-3xl font-display font-bold text-textPrimary mb-4">
                  {t('piercingBooking.payment.title', 'Deposit Payment')}
                </h1>

                <p className="text-textSecondary mb-8">
                  {t('piercingBooking.payment.description', 'Your appointment has been reserved. Please pay the deposit to confirm your booking.')}
                </p>

                {/* Deposit summary */}
                <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
                  <h3 className="text-lg font-semibold text-textPrimary mb-4">
                    {t('piercingBooking.payment.summary', 'Payment Summary')}
                  </h3>
                  <div className="divide-y divide-gray-200">
                    {getSummaryPiercings().map((piercing, index) => {
                      const details = getPiercingSummaryDetails(piercing);
                      const noteText = htmlToText(piercing.noteToPractitioner || '').trim();

                      return (
                        <div key={piercing.id} className="py-3 first:pt-0">
                          <div className="flex justify-between gap-4 mb-2">
                            <div>
                              <div className="font-medium text-textPrimary">
                                {piercingCart.length > 1 ? `Piercing ${index + 1}: ` : ''}{details.name}
                              </div>
                              {details.bodyZoneName && (
                                <div className="text-sm text-gray-500">Zone : {details.bodyZoneName}</div>
                              )}
                            </div>
                            <span className="font-medium whitespace-nowrap">{details.basePrice}€</span>
                          </div>

                          {(piercing.selectedSupplements || []).length > 0 && (
                            <div className="ml-4 mt-2 space-y-1">
                              {(piercing.selectedSupplements || []).map((suppId: string) => {
                                const supplement = supplements.find(s => s.id === suppId);
                                return supplement ? (
                                  <div key={suppId} className="flex justify-between gap-4 text-sm">
                                    <span className="text-gray-600">+ {supplement.name}</span>
                                    <span className="font-medium text-gray-700 whitespace-nowrap">+{supplement.price}€</span>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          )}

                          {noteText && (
                            <div className="mt-2 text-xs text-gray-500">
                              Note : {noteText}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-sm">
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Shop</span>
                      <span className="font-medium text-right">{shops.find(s => s.id === formData.shopId)?.name || '—'}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Date</span>
                      <span className="font-medium text-right">
                        {formData.availableDate
                          ? `${new Date(formData.availableDate).toLocaleDateString('fr-FR')} à ${formData.selectedTimeSlot}`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Client</span>
                      <span className="font-medium text-right">{`${formData.firstName || ''} ${formData.lastName || ''}`.trim() || '—'}</span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-semibold">Total</span>
                      <span className="font-semibold">{formData.totalPrice}€</span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                      <span className="text-gray-600 font-bold">
                        {t('piercingBooking.payment.depositToPay', 'Deposit to pay')}
                      </span>
                      <span className="font-bold text-accent-600">{formData.depositAmount}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        {t('piercingBooking.payment.remainingOnDay', 'Remaining on the day')}
                      </span>
                      <span className="text-gray-500">{(formData.totalPrice - formData.depositAmount).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">
                        {t('piercingBooking.payment.paymentMethod', 'Payment method')}
                      </span>
                      <span className="text-gray-500">
                        {selectedPaymentMethod ? t(selectedPaymentMethod.labelKey) : formData.paymentMethod}
                      </span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                    {error}
                  </div>
                )}

                {/* Pay button */}
                <div className="flex justify-center">
                  <button
                    onClick={handlePayDeposit}
                    disabled={payingDeposit}
                    className="inline-flex items-center gap-2 px-8 py-3 btn-primary rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    {payingDeposit ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('piercingBooking.payment.processing', 'Processing...')}</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        <span>{t('piercingBooking.payment.payDeposit', 'Pay Deposit')} — {formData.depositAmount}€</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 9:
        // Confirmation step
        return (
          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="text-center">
                <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                
                <h1 className="text-3xl font-display font-bold text-textPrimary mb-4">
                  {formData.isAnonymous
                    ? t('piercingBooking.step6.anonymousConfirmed')
                    : t('piercingBooking.step6.title')}
                </h1>
                
                <p className="text-textSecondary mb-8">
                  {formData.isAnonymous
                    ? t('piercingBooking.step6.anonymousCodeKeep')
                    : t('piercingBooking.step6.description')}
                </p>

                {formData.isAnonymous && (formData.anonymousCode || bookingData?.data?.client?.anonymousCode) && (
                  <div className="mb-8 rounded-xl border border-purple-300 bg-white px-6 py-5 text-center">
                    <p className="text-sm font-medium text-purple-800 mb-2">
                      {t('piercingBooking.step6.anonymousCodeLabel')}
                    </p>
                    <p className="text-2xl font-mono font-bold tracking-[0.2em] text-gray-900">
                      {formData.anonymousCode || bookingData?.data?.client?.anonymousCode}
                    </p>
                    <button
                      type="button"
                      onClick={async () => {
                        const code = formData.anonymousCode || bookingData?.data?.client?.anonymousCode;
                        if (!code) return;
                        try {
                          await navigator.clipboard.writeText(code);
                          setAnonymousCodeCopied(true);
                          window.setTimeout(() => setAnonymousCodeCopied(false), 2000);
                        } catch (copyError) {
                          console.warn('Failed to copy anonymous client code:', copyError);
                        }
                      }}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-700 text-white hover:bg-purple-800"
                    >
                      <Copy className="w-4 h-4" />
                      <span>
                        {anonymousCodeCopied
                          ? t('piercingBooking.step6.codeCopied')
                          : t('piercingBooking.step6.copyCode')}
                      </span>
                    </button>
                  </div>
                )}

                {/* Booking Summary */}
                <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
                  <h3 className="text-lg font-semibold text-textPrimary mb-4">
                    {t('piercingBooking.step6.bookingSummary')}
                  </h3>
                  
                  <div className="divide-y divide-gray-200">
                    {/* Show all piercings with details */}
                    {getSummaryPiercings().map((piercing, index) => {
                      const details = getPiercingSummaryDetails(piercing);

                      return (
                        <div key={piercing.id} className="py-3 first:pt-0">
                          <div className="flex justify-between gap-4 mb-2">
                            <span className="text-gray-600 font-medium">
                              {piercingCart.length > 1 ? `Piercing ${index + 1}: ` : ''}{details.name}
                              {details.bodyZoneName && ` (${details.bodyZoneName})`}
                            </span>
                            <span className="font-medium whitespace-nowrap">{details.basePrice}€</span>
                          </div>
                          
                          {/* Show supplements for this piercing */}
                          {(piercing.selectedSupplements || []).length > 0 && (
                            <div className="ml-4 mt-2 space-y-1">
                              {(piercing.selectedSupplements || []).map((suppId: string) => {
                                const supplement = supplements.find(s => s.id === suppId);
                                return supplement ? (
                                  <div key={suppId} className="flex justify-between text-sm">
                                    <span className="text-gray-600">+ {supplement.name}</span>
                                    <span className="font-medium text-gray-700">+{supplement.price}€</span>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between border-t border-gray-200 pt-3">
                    <span className="text-gray-600 font-bold">Total</span>
                    <span className="font-bold text-accent-600">{formData.totalPrice}€</span>
                  </div>

                  {/* Deposit payment info */}
                    {formData.depositAmount > 0 && (
                      <>
                        <div className="flex justify-between mt-2">
                          <span className="text-gray-600">{t('piercingBooking.payment.depositToPay', 'Deposit')}</span>
                          <span className="font-medium">{formData.depositAmount}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('piercingBooking.step6.depositStatus', 'Status')}</span>
                          <span className="inline-flex items-center gap-1 font-medium text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            {t('piercingBooking.step6.depositPaid', 'Paid')}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">{t('piercingBooking.payment.remainingOnDay', 'Remaining on the day')}</span>
                          <span className="text-gray-500">{(formData.totalPrice - formData.depositAmount).toFixed(2)}€</span>
                        </div>
                      </>
                    )}
                    
                    <div className="flex justify-between mt-4">
                      <span className="text-gray-600">Shop</span>
                      <span className="font-medium">{shops.find(s => s.id === formData.shopId)?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date</span>
                      <span className="font-medium">
                        {new Date(formData.availableDate).toLocaleDateString('fr-FR')} à {formData.selectedTimeSlot}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('piercingBooking.step6.client')}</span>
                      <span className="font-medium text-right">
                        {formData.isAnonymous ? (
                          <>
                            {formatPersonName(formData.firstName, formData.lastName) ? (
                              <span className="block">{formatPersonName(formData.firstName, formData.lastName)}</span>
                            ) : null}
                            <span className="block">{t('piercingBooking.clientCreated.anonymousLabel')}</span>
                            {formData.anonymousCode ? (
                              <span className="block font-mono">{formData.anonymousCode}</span>
                            ) : null}
                          </>
                        ) : (
                          formatPersonName(formData.firstName, formData.lastName)
                        )}
                      </span>
                    </div>
                </div>

                {/* Next Steps */}
                <div className="bg-blue-50 rounded-lg p-6 mb-8 text-left">
                  <h3 className="text-lg font-semibold text-textPrimary mb-4">
                    {t('piercingBooking.step6.nextSteps')}
                  </h3>
                  
                  <ul className="space-y-2 text-sm text-blue-800">
                    {!formData.isAnonymous && (
                      <>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('piercingBooking.step6.confirmationEmail')}
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('piercingBooking.step6.reminderSms')}
                    </li>
                      </>
                    )}
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('piercingBooking.step6.arriveEarly')}
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('piercingBooking.step6.bringId')}
                    </li>
                  </ul>
                </div>

                {/* QR Code + check-in code */}
                {bookingData?.data?.appointment?.qrCodeImage && (
                  <div className="text-center py-6">
                    <img
                      src={bookingData.data.appointment.qrCodeImage}
                      alt={t('piercingBooking.step6.qrCodeAlt', 'QR Code')}
                      className="w-40 h-40 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      {t('piercingBooking.step6.qrCodeHint', 'Present this QR code when you arrive')}
                    </p>
                    {bookingData.data.appointment.checkInCode && (
                      <div className="mt-4 inline-flex flex-col items-center rounded-xl border border-gray-200 bg-gray-50 px-6 py-3">
                        <span className="text-xs uppercase tracking-wider text-gray-500">
                          {t('piercingBooking.step6.checkInCodeLabel', "Code d'accueil")}
                        </span>
                        <span className="mt-1 text-2xl font-bold tracking-[0.35em] text-textPrimary">
                          {bookingData.data.appointment.checkInCode}
                        </span>
                        <span className="mt-1 text-xs text-gray-500">
                          {t('piercingBooking.step6.checkInCodeHint', 'Donnez ce code à l’accueil si le scan n’est pas possible')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="btn-secondary"
                  >
                    {t('piercingBooking.step6.backToHome')}
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="btn-primary"
                  >
                    {t('piercingBooking.step6.bookAnother')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleNext = async () => {
    console.log('handleNext called for step:', currentStep);
    setError(null);
    const isValid = await validateStep(currentStep);
    console.log('Validation result:', isValid);
    if (!isValid) return;

    // If we're on payment step, submit instead of advancing
    if (currentStep === 7) {
      await handleSubmit();
      return;
    }

    // Special case: after piercing selection (or supplements when enabled),
    // add the piercing to the cart and show the add-another modal.
    const shouldAddPiercingToCart = supplementsStepEnabled
      ? currentStep === 4
      : currentStep === 3;
    if (shouldAddPiercingToCart) {
      // If we're navigating back and editing an existing piercing (editingPiercingId is set), update it
      if (isNavigatingBack && editingPiercingId) {
        // Update the existing piercing in cart (this handles changes to supplements)
        addPiercingToCart(isAddingAnotherPiercing);
        setIsNavigatingBack(false); // Reset flag
        setIsAddingAnotherPiercing(false); // Reset flag
        setShowAddAnotherModal(true);
        return;
      }
      
      // If we're navigating back (data was restored from cart), check if this exact piercing is already in cart
      if (isNavigatingBack) {
        // Helper function to compare supplements arrays
        const supplementsEqual = (arr1: string[], arr2: string[]): boolean => {
          if (arr1.length !== arr2.length) return false;
          const sorted1 = [...arr1].sort();
          const sorted2 = [...arr2].sort();
          return sorted1.every((val, idx) => val === sorted2[idx]);
        };

        const supplementsToCheck = Array.isArray(formData.selectedSupplements) 
          ? [...formData.selectedSupplements] 
          : [];

        const alreadyInCart = piercingCart.some(p => 
          p.bodyZoneId === formData.bodyZoneId &&
          p.piercingTypeId === formData.piercingTypeId &&
          supplementsEqual(
            Array.isArray(p.selectedSupplements) ? p.selectedSupplements : [],
            supplementsToCheck
          )
        );

        // If already in cart and we're just navigating (not explicitly adding another), skip adding
        if (PIERCING_COMBINATION_RULES_ENABLED && alreadyInCart && !isAddingAnotherPiercing) {
          console.log('Piercing already in cart from navigation, skipping duplicate add');
          setIsNavigatingBack(false); // Reset flag
          setIsAddingAnotherPiercing(false); // Reset flag
          setShowAddAnotherModal(true);
          return; // Don't add to cart, just show modal
        }
        // Reset navigation flag since we've checked
        setIsNavigatingBack(false);
      }

      // Add to cart (either new piercing or explicitly adding another)
      addPiercingToCart(isAddingAnotherPiercing);
      setIsAddingAnotherPiercing(false); // Reset flag after use
      setShowAddAnotherModal(true);
      return; // Don't advance to next step automatically
    }

    // Handle step transitions with OTP logic
    if (currentStep === 1) {
      if (isAuthenticated && user) {
        // User confirmed at step 1: set client data and go to appointment choice
        const clientId = (user as any).id;
        const clientData = {
          id: clientId,
          firstName: (user as any).firstName || '',
          lastName: (user as any).lastName || '',
          email: (user as any).email || '',
          phone: (user as any).phone || '',
          dateOfBirth: user.dateOfBirth || '',
          preferredLanguage: (user as any).preferredLanguage || formData.preferredLanguage,
          emergencyContact: formData.emergencyContact
        };
        setFormData(prev => ({
          ...prev,
          clientId,
          preferredLanguage: clientData.preferredLanguage,
          firstName: clientData.firstName,
          lastName: clientData.lastName,
          email: clientData.email,
          phone: clientData.phone,
          dateOfBirth: clientData.dateOfBirth,
          isMinor: (user as any).isMinor ?? null,
          emergencyContact: clientData.emergencyContact || prev.emergencyContact
        }));
        setExistingClient({
          id: clientId,
          firstName: clientData.firstName,
          lastName: clientData.lastName,
          email: clientData.email,
          phone: clientData.phone,
          dateOfBirth: clientData.dateOfBirth,
          emergencyContact: clientData.emergencyContact,
        });
        setCurrentStep(5);
      } else if (formData.isAnonymous && formData.clientId) {
        setCurrentStep(5);
      } else if (otpSent) {
        setCurrentStep(2); // Go to OTP verification
      } else {
        // No OTP: proceed to contact details
        setFormData(prev => ({ ...prev, email: contactInfo.email || '', phone: contactInfo.phone || '' }));
        setCurrentStep(6);
      }
    } else if (currentStep === 2) {
      // After OTP, skip CustomerDetailsStep (step 6) — data already populated
      if (existingClient) {
        setFormData(prev => ({ ...prev, clientId: existingClient.id }));
        setCurrentStep(5);
      } else {
        setCurrentStep(6);
      }
    } else if (currentStep === 5) {
      // After appointment selection, go to payment step
      setCurrentStep(7);
    } else if (currentStep === 6) {
      // After contact details, go to appointment selection
      setCurrentStep(5);
    } else if (currentStep === 7) {
      setCurrentStep(6); // From payment back to contact details
    } else {
      setCurrentStep(prev => Math.min(9, prev + 1));
    }
    scrollBookingIntoView(bookingTopRef.current);
  };

  const handlePrevious = () => {
    setError(null);
    // Custom back mapping around identification/contact/appointment
    if (currentStep === 1) {
      if (formData.isAnonymous && !formData.clientId) {
        setFormData((prev) => ({
          ...prev,
          isAnonymous: false,
          anonymousCode: undefined,
        }));
        scrollBookingIntoView(bookingTopRef.current);
        return;
      }
      restorePiercingSelectionFromCart();
      setCurrentStep(3); // From initial contact back to piercing choice
    } else if (currentStep === 2) {
      setOtpVerified(false);
      setCurrentStep(1); // From OTP back to identification
    } else if (currentStep === 6) {
      // From contact details back to identification
      setCurrentStep(1);
    } else if (currentStep === 7) {
      setCurrentStep(5); // From payment back to appointment selection
    } else if (currentStep === 5) {
      // From appointment/slot selection back to contact details
      if (isAuthenticated && user) {
        setCurrentStep(1);
      } else if (formData.isAnonymous) {
        setCurrentStep(1);
      } else {
        setCurrentStep(6);
      }
    } else if (currentStep === 4) {
      if (!supplementsStepEnabled) {
        setCurrentStep(3);
      } else {
      // From supplements back to piercing choice
      // Restore piercing selection from the last piercing in cart if available
      restorePiercingSelectionFromCart();
      setCurrentStep(3);
      }
    } else if (currentStep === 3) {
      // Stop at step 3 (first step) - don't go further back
      return;
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
    scrollBookingIntoView(bookingTopRef.current);
  };

  // Validate current step before proceeding
  const validateCurrentStep = async (): Promise<boolean> => {
    const errors: ValidationErrors = {};

    switch (currentStep) {
      case 1:
        // When authenticated, step 1 shows confirmation UI - no piercing fields to validate
        if (isAuthenticated && user) {
          break;
        }
        if (!formData.bodyZoneId) {
          errors.bodyZoneId = 'Veuillez sélectionner une zone du corps';
        }
        if (!formData.piercingTypeId) {
          errors.piercingTypeId = 'Veuillez sélectionner un type de piercing';
        }
        if (!formData.availableDate) {
          errors.availableDate = 'Veuillez sélectionner une date';
        }
        break;

      case 3:
        if (!formData.shopId) {
          errors.shopId = 'Veuillez sélectionner un shop';
        }
        if (!formData.selectedTimeSlot) {
          errors.selectedTimeSlot = 'Veuillez sélectionner un créneau horaire';
        }
        break;

      case 4:
        // Only validate if we're in the form step and not using existing client
        if (clientCheckStep === 'form' && !existingClient) {
          if (!formData.firstName.trim()) {
            errors.firstName = 'Le prénom est requis';
          }
          
          // Validate email format if provided
          if (formData.email && formData.email.trim().length > 0) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
              errors.email = 'Format d\'email invalide';
            }
          }
          
          // Validate phone format if provided
          if (formData.phone && formData.phone.trim().length > 0) {
            const phoneDigits = formData.phone.replace(/\D/g, '');
            if (phoneDigits.length < 8) {
              errors.phone = 'Le numéro de téléphone doit contenir au moins 8 chiffres';
            }
          }
        }

        // Check for existing client
        const hasEmail = !!(formData.email && formData.email.trim().length > 0);
        const hasPhone = !!(formData.phone && formData.phone.trim().length > 0);
        if ((hasEmail || hasPhone) && !errors.email && !errors.phone) {
          try {
            const data = await clientApi.checkDuplicate({
              email: formData.email || null,
              phone: formData.phone || null,
            });
            
            if (data.success && data.exists) {
              setExistingClient(data.client);
              setShowExistingClientModal(true);
              return false; // Don't proceed to next step, wait for user decision
            }
          } catch (error) {
            console.error('Error checking for existing client:', error);
          }
        }
        break;

      case 5:
        if (!formData.paymentMethod) {
          errors.paymentMethod = 'Veuillez sélectionner une méthode de paiement';
        }
        if (formData.totalPrice <= 0) {
          errors.totalPrice = 'Le prix total doit être supérieur à 0';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCloseExistingClientModal = () => {
    setShowExistingClientModal(false);
    setExistingClient(null);
  };

  const handleCloseAddAnotherModal = () => {
    setShowAddAnotherModal(false);
    if (piercingCart.length > 0) {
      restorePiercingSelectionFromCart();
    } else {
      resetPiercingSelectionForm();
    }
  };

  const handleChooseAnotherTimeSlot = () => {
    setBookingConflictMessage(null);
    setBookingConflictSelection(null);
    setError(null);
    setFormData((prev) => ({ ...prev, selectedTimeSlot: '' }));
    setPiercingConflict(null);
    lastPiercingConflictKey.current = null;
    setCurrentStep(5);
    requestAnimationFrame(() => scrollBookingIntoView(bookingTopRef.current));
  };

  const handleExistingClientDecision = (useExisting: boolean) => {
    if (useExisting && existingClient) {
      // Use existing client data
      setFormData(prev => ({
        ...prev,
        firstName: existingClient.firstName,
        lastName: existingClient.lastName,
        email: existingClient.email,
        phone: existingClient.phone
      }));
    }
    setShowExistingClientModal(false);
    setExistingClient(null);
    
    // Proceed to next step
    setCurrentStep(prev => prev + 1);
    scrollBookingIntoView(bookingTopRef.current);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Final validation
      const isValid = await validateCurrentStep();
      if (!isValid) {
        setSubmitting(false);
        return;
      }

      // Piercer assignment is handled by planning; no practitioner is set here.

      // Prepare booking data - Enhanced Planning format
      const piercingsPayload = piercingCart.length > 0
        ? piercingCart.map(piercing => ({
            bodyZoneId: piercing.bodyZoneId,
            piercingTypeId: piercing.piercingTypeId,
            selectedSupplements: piercing.selectedSupplements,
            noteToPractitioner: piercing.noteToPractitioner
          }))
        : [{
            bodyZoneId: formData.bodyZoneId,
            piercingTypeId: formData.piercingTypeId,
            selectedSupplements: formData.selectedSupplements,
            noteToPractitioner: htmlToText(formData.noteToPractitioner || '').trim()
              ? formData.noteToPractitioner
              : undefined
          }];

      const bookingPayload: any = {
        // Multi-piercing support - send piercings array (fallback to current selection)
        piercings: piercingsPayload,
        
        // Shared appointment details
        shopId: formData.shopId,
        availableDate: formData.availableDate,
        selectedTimeSlot: formData.selectedTimeSlot,
        
        // Contact details - use clientId if available, otherwise fallback to form data
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        // Format dateOfBirth as date string (YYYY-MM-DD) if it's an ISO string
        dateOfBirth: formData.dateOfBirth 
          ? (formData.dateOfBirth.includes('T') 
              ? formData.dateOfBirth.split('T')[0] 
              : formData.dateOfBirth)
          : undefined,
        emergencyContact: formData.emergencyContact,
        
        // Payment details
        paymentMethod: formData.paymentMethod,
        totalPrice: formData.totalPrice,
        depositAmount: formData.depositAmount,
        notes: formData.notes,
        journeyId: journeyIdRef.current || undefined,
      };

      // Use clientId from formData (set by CustomerDetailsStep)
      if (formData.clientId) {
        bookingPayload.clientId = formData.clientId;
      }

      if (formData.clientId && formData.shopId && formData.availableDate && formData.selectedTimeSlot) {
        const conflictCheck = await bookingApi.checkPiercingConflict({
          clientId: formData.clientId,
          shopId: formData.shopId,
          availableDate: formData.availableDate,
          selectedTimeSlot: formData.selectedTimeSlot,
          piercings: piercingsPayload,
        });

        if (conflictCheck.success && conflictCheck.data?.conflict) {
          setError(null);
          setBookingConflictSelection({
            date: formData.availableDate,
            time: formData.selectedTimeSlot,
          });
          setBookingConflictMessage(
            conflictCheck.data.conflictReason === 'shop_unavailable'
              ? 'Ce créneau est déjà réservé dans ce shop.'
              : 'Ce client a déjà un rendez-vous sur ce créneau horaire.',
          );
          setSubmitting(false);
          return;
        }
      }

      console.log('📤 Submitting booking:', bookingPayload);

      const data = await bookingApi.bookPiercing(bookingPayload);

      if (data.success) {
        console.log('✅ Booking successful:', data);
        const bookedClient = data.data?.client;
        if (bookedClient?.isAnonymous || bookedClient?.anonymousCode) {
          setFormData((prev) => ({
            ...prev,
            isAnonymous: true,
            anonymousCode: bookedClient.anonymousCode || prev.anonymousCode,
            firstName: bookedClient.firstName || prev.firstName,
            lastName: bookedClient.lastName || prev.lastName,
          }));
        }
        setBookingData(data);
        
        // Check if deposit payment is required
        const appt = data.data?.appointment;
        if (appt?.depositRequired && appt?.depositStatus === 'PENDING') {
          setDepositPending(true);
          setCurrentStep(8); // payment step
        } else {
          setBookingConfirmed(true);
          setCurrentStep(9); // confirmation step (was 8)
        }
        scrollBookingIntoView(bookingTopRef.current);
      } else {
        console.error('❌ Booking response error:', data);

        if (
          data.httpStatus === 409
          || data.code === 'CLIENT_SLOT_CONFLICT'
          || data.message?.includes('déjà un rendez-vous sur ce créneau')
        ) {
          setError(null);
          setBookingConflictSelection({
            date: formData.availableDate,
            time: formData.selectedTimeSlot,
          });
          setBookingConflictMessage(
            data.message || 'Ce client a déjà un rendez-vous sur ce créneau horaire.',
          );
          return;
        }
        
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          const errorMap: ValidationErrors = {};
          data.errors.forEach((err: any) => {
            errorMap[err.field] = err.message;
          });
          setValidationErrors(errorMap);
          
          // Show detailed error message
          const errorMessages = data.errors.map((err: any) => `${err.field}: ${err.message}`).join(', ');
          setError(`Erreurs de validation: ${errorMessages}`);
        } else {
          setError(data.message || 'Échec de la réservation du rendez-vous');
        }
      }
    } catch (error) {
      console.error('❌ Booking submission error:', error);
      setError('Échec de la réservation du rendez-vous');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayDeposit = async () => {
    try {
      setPayingDeposit(true);
      setError(null);
      const appointmentId = bookingData?.data?.appointment?.id;
      if (!appointmentId) {
        setError('Appointment ID not found');
        return;
      }

      // Create a Stripe Checkout Session and redirect
      const response = await bookingApi.createCheckoutSession(appointmentId);
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return;
      }
      setError(response.message || t('piercingBooking.payment.paymentFailed', 'Payment failed'));
    } catch (err) {
      console.error('Deposit payment error:', err);
      setError(t('piercingBooking.payment.paymentFailed', 'Payment failed'));
    } finally {
      setPayingDeposit(false);
    }
  };

  const selectedPiercing = piercingTypes.find(p => p.id === formData.piercingTypeId);
  const selectedShop = shops.find(s => s.id === formData.shopId);
  const isBusy = loading || submitting;
  const clientDisplayName = formatPersonName(formData.firstName, formData.lastName);
  const clientIdentityLine = formData.isAnonymous
    ? [
        clientDisplayName,
        t('piercingBooking.clientCreated.anonymousLabel'),
        formData.anonymousCode,
      ].filter(Boolean).join(' • ')
    : [clientDisplayName, formData.email?.trim(), formData.phone?.trim()]
        .filter(Boolean)
        .join(' • ');

  const customerSummary = useMemo(() => {
    const isAnonymous = Boolean(existingClient?.isAnonymous || formData.isAnonymous);
    const anonymousCode = existingClient?.anonymousCode || formData.anonymousCode;
    const name = existingClient
      ? formatPersonName(existingClient.firstName, existingClient.lastName)
      : clientDisplayName;
    if (!existingClient && !formData.clientId && !name && !isAnonymous) return null;
    return {
      name: name || undefined,
      email: isAnonymous ? undefined : (existingClient?.email?.trim() || formData.email?.trim() || undefined),
      phone: isAnonymous ? undefined : (existingClient?.phone?.trim() || formData.phone?.trim() || undefined),
      isAnonymous,
      anonymousCode: anonymousCode || undefined,
    };
  }, [
    existingClient,
    formData.clientId,
    formData.isAnonymous,
    formData.anonymousCode,
    clientDisplayName,
    formData.email,
    formData.phone,
  ]);

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {bookingData?.data?.client?.isNew && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 mb-6 text-left">
                {t('piercingBooking.step6.newClient')}
              </div>
            )}
            <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-display font-bold text-textPrimary mb-4">
              {t('piercingBooking.step6.title')}
            </h1>
            
            <p className="text-textSecondary mb-8">
              {t('piercingBooking.step6.description')}
            </p>

            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step6.bookingSummary')}
              </h3>
              
              <div className="divide-y divide-gray-200">
                {/* Show all piercings with details */}
                {getSummaryPiercings().map((piercing, index) => {
                  const details = getPiercingSummaryDetails(piercing);

                  return (
                    <div key={piercing.id} className="py-3 first:pt-0">
                      <div className="flex justify-between gap-4 mb-2">
                        <span className="text-gray-600 font-medium">
                          {piercingCart.length > 1 ? `Piercing ${index + 1}: ` : ''}{details.name}
                          {details.bodyZoneName && ` (${details.bodyZoneName})`}
                        </span>
                        <span className="font-medium whitespace-nowrap">{details.basePrice}€</span>
                      </div>
                      
                      {/* Show supplements for this piercing */}
                      {(piercing.selectedSupplements || []).length > 0 && (
                        <div className="ml-4 mt-2 space-y-1">
                          {(piercing.selectedSupplements || []).map((suppId: string) => {
                            const supplement = supplements.find(s => s.id === suppId);
                            return supplement ? (
                              <div key={suppId} className="flex justify-between text-sm">
                                <span className="text-gray-600">+ {supplement.name}</span>
                                <span className="font-medium text-gray-700">+{supplement.price}€</span>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between border-t border-gray-200 pt-3">
                <span className="text-gray-600 font-bold">Total</span>
                <span className="font-bold text-accent-600">{formData.totalPrice}€</span>
              </div>

              <div className="flex justify-between mt-4">
                  <span className="text-gray-600">Shop</span>
                  <span className="font-medium">{selectedShop?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-medium">
                    {new Date(formData.availableDate).toLocaleDateString('fr-FR')} à {formData.selectedTimeSlot}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('piercingBooking.step6.client')}</span>
                  <span className="font-medium">{formatPersonName(formData.firstName, formData.lastName)}</span>
                </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-textPrimary mb-4">
                {t('piercingBooking.step6.nextSteps')}
              </h3>
              
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('piercingBooking.step6.confirmationEmail')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('piercingBooking.step6.reminderSms')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('piercingBooking.step6.arriveEarly')}
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('piercingBooking.step6.bringId')}
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="btn-secondary"
              >
                {t('piercingBooking.step6.backToHome')}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                {t('piercingBooking.step6.bookAnother')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={bookingTopRef}
      className={`min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 pt-24 pb-24 md:pb-12 px-4 ${submitting ? 'cursor-wait' : ''}`}
    >
      {showClientCreatedToast && clientCreatedNotice && (
        <div className="fixed top-24 right-4 z-50 w-full max-w-sm">
          <div className="rounded-lg border border-green-200 bg-white shadow-lg px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-green-800">
                <p className="font-semibold">{t('piercingBooking.clientCreated.toastTitle')}</p>
                <p>
                  {clientCreatedNotice === 'account'
                    ? t('piercingBooking.clientCreated.toastAccount')
                    : clientCreatedNotice === 'anonymous'
                      ? t('piercingBooking.clientCreated.toastAnonymous')
                      : t('piercingBooking.clientCreated.toastGuest')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowClientCreatedToast(false)}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        {currentStep < 8 && (
          <div className="text-center mb-6 md:mb-12">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-textPrimary mb-2 md:mb-4">
              {t('piercingBooking.title')}
            </h1>
            <p className="text-sm md:text-base text-textSecondary">
              {t('piercingBooking.subtitle')}
            </p>
          </div>
        )}

        {/* Progress + Form — single panel (no double border) */}
        <div className="fo-panel bg-white rounded-2xl p-4 sm:p-6 lg:p-8">
        {currentStep < 8 && (
          <>
            <BookingStepper
              variant="numbered"
              steps={piercingProgressSteps}
              currentIndex={mapPiercingDisplayStep(currentStep) - 1}
            />
            <LandscapeHint active={currentStep === 5} />
          </>
        )}

        {/* Form Content */}
          {/* Global Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Render the regular booking flow — keep inputs clickable while loading (buttons stay disabled). */}
          <div className={isBusy ? 'opacity-60' : ''}>
            {renderStep()}
          </div>

          {/* Navigation Buttons */}
          {currentStep < 8 && currentStep !== 6 && (
            <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 mt-6 px-4 sm:px-6 lg:px-8 py-3 border-t border-gray-200 bg-white/95 backdrop-blur flex items-center justify-between gap-3 md:static md:z-auto md:mx-0 md:mt-8 md:px-0 md:py-0 md:border-0 md:bg-transparent md:backdrop-blur-none">
              {currentStep > 1 || currentStep === 1 ? (
                <button
                  onClick={handlePrevious}
                  disabled={isBusy}
                  className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>{t('common.previous')}</span>
                </button>
              ) : (
                <div></div>
              )}
              
              {currentStep === 1 && formData.isAnonymous && !formData.clientId ? (
              <button
                type="submit"
                form="piercing-anonymous-guest-form"
                disabled={isBusy || anonymousFormBusy}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {(isBusy || anonymousFormBusy) && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>
                  {anonymousFormBusy
                    ? t('customerDetails.guest.creating')
                    : t('common.next')}
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
              ) : (
              <button
                onClick={handleNext}
                disabled={isBusy || (currentStep === 1 && showClientConfirmation)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>
                  {currentStep === 7 && submitting
                    ? t('piercingBooking.step5.processing')
                    : currentStep === 7
                      ? t('piercingBooking.step5.confirmBooking')
                      : isBusy
                        ? t('common.loading')
                        : t('common.next')}
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
              )}
            </div>
          )}
        </div>
      </div>

      {openLegalModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-modal-title"
          onMouseDown={() => setOpenLegalModal(null)}
        >
          <div
            className="modal-panel flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ModalHeader
              title={(
                <h2 id="legal-modal-title" className="text-xl font-semibold text-textPrimary">
                  {t(`legal.pages.${openLegalModal}.title`)}
                </h2>
              )}
              onClose={() => setOpenLegalModal(null)}
              className="border-b border-gray-200 px-5 py-4 sm:px-6"
              closeClassName="mt-0 mr-0"
            />
            <div className="overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
              {openLegalModal === 'terms' ? (
                <LegalPageContent page="terms" embedded />
              ) : (
                <RefundPolicyContent embedded />
              )}
            </div>
          </div>
        </div>
      )}

      {bookingConflictMessage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="booking-conflict-title"
          aria-describedby="booking-conflict-description"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-2xl border-2 border-red-500 bg-white shadow-2xl">
            <div className="bg-red-500 px-6 py-5 text-white">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <AlertTriangle className="h-7 w-7" aria-hidden="true" />
                </div>
                <h2 id="booking-conflict-title" className="text-xl font-bold sm:text-2xl">
                  Ce créneau est déjà réservé
                </h2>
              </div>
            </div>

            <div className="p-6">
              <p
                id="booking-conflict-description"
                className="text-lg font-semibold leading-relaxed text-gray-900"
              >
                {bookingConflictMessage}
              </p>
              <p className="mt-3 text-gray-600">
                La réservation n’a pas été enregistrée. Sélectionnez un autre horaire pour continuer.
              </p>

              {bookingConflictSelection && (
                <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center">
                  <p className="text-sm font-medium text-red-700">Créneau sélectionné</p>
                  <p className="mt-1 text-lg font-bold text-red-900">
                    {new Date(`${bookingConflictSelection.date}T12:00:00`).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}{' '}
                    à {bookingConflictSelection.time}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleChooseAnotherTimeSlot}
                autoFocus
                className="btn-primary mt-6 w-full justify-center py-3 text-base"
              >
                Choisir un autre créneau
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing Client Modal */}
      {showExistingClientModal && existingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="modal-panel rounded-lg max-w-md w-full">
            <div className="p-6">
              <ModalHeader
                title="Client existant trouvé"
                onClose={handleCloseExistingClientModal}
                className="mb-4"
              />
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm mb-2">
                  Un compte client existe déjà avec ces informations:
                </p>
                <div className="text-sm">
                  <p><strong>Nom:</strong> {formatPersonName(existingClient.firstName, existingClient.lastName)}</p>
                  <p><strong>Email:</strong> {existingClient.email}</p>
                  <p><strong>Téléphone:</strong> {existingClient.phone}</p>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4">
                {t('piercingBooking.existingClient.verificationInfo', {
                  method: contactInfo.email.trim() 
                    ? t('piercingBooking.existingClient.verificationInfoEmail')
                    : t('piercingBooking.existingClient.verificationInfoPhone')
                })}
              </p>

              <p className="text-gray-600 text-sm mb-6">
                Souhaitez-vous utiliser ce compte existant ou créer un nouveau compte?
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <button
                  type="button"
                  onClick={handleCloseExistingClientModal}
                  className="btn-secondary sm:order-first"
                >
                  {t('common.cancel')}
                </button>
                <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => handleExistingClientDecision(false)}
                  className="btn-secondary"
                >
                  Créer nouveau compte
                </button>
                <button
                  onClick={() => handleExistingClientDecision(true)}
                  className="btn-primary"
                >
                  Utiliser compte existant
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Another Piercing Modal */}
      {showAddAnotherModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="modal-panel rounded-lg max-w-md w-full">
            <div className="p-6">
              <ModalHeader
                title={t('piercingBooking.multiPiercing.title')}
                onClose={handleCloseAddAnotherModal}
                className="mb-4"
              />
              
              <p className="text-gray-600 text-sm">
                {t('piercingBooking.multiPiercing.description')} <strong className="text-red-600">{t('piercingBooking.multiPiercing.condition')}</strong>
              </p>
              <p className="mt-1 text-sm  mb-4"><strong className="text-red-600 d-block">{t('piercingBooking.multiPiercing.condition2')}</strong> {t('piercingBooking.multiPiercing.condition3')}</p>

              {/* Cart Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-textPrimary mb-3">
                  {t('piercingBooking.cart.title')}
                </h4>
                <div className="divide-y divide-gray-200">
                  {piercingCart.map((piercing) => {
                    const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
                    const bodyZone = bodyZones.find(bz => bz.id === piercing.bodyZoneId);
                    
                    return (
                      <div key={piercing.id} className="py-2 first:pt-0">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">
                            {piercingType?.name} ({bodyZone?.name})
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">
                              {piercingType?.basePrice || 0}€
                            </span>
                            <button
                              onClick={() => removePiercingFromCart(piercing.id)}
                              className="fo-link text-xs inline-flex items-center space-x-1 hover:no-underline"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>{t('piercingBooking.cart.remove')}</span>
                            </button>
                          </div>
                        </div>
                        {(piercing.selectedSupplements || []).length > 0 && (
                          <div className="ml-2 mt-1 space-y-1">
                            {(piercing.selectedSupplements || []).map((suppId: string) => {
                              const supplement = supplements.find(s => s.id === suppId);
                              return supplement ? (
                                <div key={suppId} className="text-xs text-gray-600">
                                  + {supplement.name} (+{supplement.price}€)
                                </div>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-gray-200 pt-2 font-medium">
                  {(() => {
                      // Calculate total directly from cart to ensure accuracy
                      // Use the same calculation logic as the individual items above
                      let calculatedTotal = 0;
                      piercingCart.forEach((piercing) => {
                        const piercingType = piercingTypes.find(p => p.id === piercing.piercingTypeId);
                        if (!piercingType) {
                          console.warn(`Piercing type not found for ID: ${piercing.piercingTypeId}`);
                          return;
                        }
                        
                        const basePrice = piercingType.basePrice || 0;
                        const supplementsArray = Array.isArray(piercing.selectedSupplements) 
                          ? piercing.selectedSupplements 
                          : [];
                        
                        console.log(`[Modal Total] Piercing: ${piercingType.name}`);
                        console.log(`[Modal Total] - basePrice: ${basePrice}`);
                        console.log(`[Modal Total] - selectedSupplements array:`, supplementsArray);
                        console.log(`[Modal Total] - supplements array length:`, supplements.length);
                        
                        let supplementsTotal = 0;
                        supplementsArray.forEach((suppId: string) => {
                          const supplement = supplements.find(s => s.id === suppId);
                          if (supplement) {
                            supplementsTotal += supplement.price;
                            console.log(`[Modal Total] - Found supplement: ${supplement.name} (${suppId}) = ${supplement.price}€`);
                          } else {
                            console.warn(`[Modal Total] - Supplement not found for ID: ${suppId}`);
                            console.log(`[Modal Total] - Available supplement IDs:`, supplements.map(s => s.id));
                          }
                        });
                        
                        const itemTotal = basePrice + supplementsTotal;
                        console.log(`[Modal Total] - Item total: ${itemTotal}€ (base: ${basePrice}€ + supplements: ${supplementsTotal}€)`);
                        calculatedTotal += itemTotal;
                      });
                      
                      console.log(`[Modal Total] FINAL TOTAL: ${calculatedTotal}€`);
                      
                      return t('piercingBooking.cart.total', { amount: calculatedTotal });
                    })()}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAddAnotherPiercing}
                  className="btn-secondary flex-1"
                >
                  {t('piercingBooking.multiPiercing.addAnother')}
                </button>
                <button
                  onClick={handleCompleteBooking}
                  className="btn-primary flex-1"
                >
                  {t('piercingBooking.multiPiercing.completeBooking')}
                </button>
                </div>
                <button
                  type="button"
                  onClick={handleCloseAddAnotherModal}
                  className="btn-secondary sm:self-end"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
